/**
 * Environment Variable Schemas
 *
 * Zod schemas for validating environment variables.
 * Call .parse(process.env) at startup to fail fast with clear errors.
 */

import { z } from 'zod';

// =============================================================================
// Helper for Optional with Default
// =============================================================================

/**
 * Create a string schema that falls back to a default if empty/undefined
 */
function stringWithDefault(defaultValue: string) {
  return z
    .string()
    .optional()
    .transform((val) => val || defaultValue);
}

/**
 * Create an integer schema from string env var with default
 */
function intWithDefault(defaultValue: number) {
  return z
    .string()
    .optional()
    .transform((val) => (val ? parseInt(val, 10) : defaultValue))
    .refine((val) => !isNaN(val), { message: 'Must be a valid integer' });
}

// =============================================================================
// Server Environment Schema
// =============================================================================

// Vars that are optional in development but required once NODE_ENV=production.
// These are the silent-fallback traps we hit during the Vestra infra migration —
// WS_BASE_URL silently defaulted to localhost and took out agent sandboxes until
// we noticed. Failing fast at boot beats chasing ECONNREFUSED in Railway logs.
const PROD_REQUIRED_VARS: Array<{
  key: 'WS_BASE_URL' | 'SUPABASE_URL' | 'SUPABASE_SERVICE_KEY' | 'SUPABASE_ANON_KEY' | 'ANTHROPIC_API_KEY' | 'VESTRA_INTERNAL_TOKEN';
  reason: string;
}> = [
  { key: 'WS_BASE_URL', reason: 'sandbox→server proxy (ANTHROPIC_BASE_URL is derived from this)' },
  { key: 'SUPABASE_URL', reason: 'database + auth' },
  { key: 'SUPABASE_SERVICE_KEY', reason: 'server-side admin writes' },
  { key: 'SUPABASE_ANON_KEY', reason: 'client-facing token minting' },
  { key: 'ANTHROPIC_API_KEY', reason: 'credential proxy injects this into sandbox requests' },
  { key: 'VESTRA_INTERNAL_TOKEN', reason: 'sandbox→server interaction registration (POST /api/internal/interactions)' },
];

/** The subset of PROD_REQUIRED_VARS the nami worker entry needs (C3a; see the
 *  superRefine below). Blaxel is checked separately, for both entries. */
const WORKER_PROD_REQUIRED_KEYS: ReadonlySet<string> = new Set(['SUPABASE_URL', 'SUPABASE_SERVICE_KEY']);

export const ServerEnvSchema = z.object({
  // Server configuration
  PORT: intWithDefault(3005),
  HOST: stringWithDefault('0.0.0.0'),

  // CORS configuration
  CORS_ORIGIN: stringWithDefault('http://localhost:3000'),

  // WebSocket configuration (for agent connections)
  WS_BASE_URL: z.string().url().optional(),

  // Move 4 — the AI proxy lane. Origin of the rarely-deployed Railway service that
  // carries the five MODEL proxy lanes (anthropic/openai/fireworks/meta/xai). UNSET =
  // the model base URLs derive from WS_BASE_URL exactly as before, byte-identical.
  // Parallel/Exa and VESTRA_SERVER_URL always stay on WS_BASE_URL — the metered lanes
  // never move.
  AI_LANE_BASE_URL: z.string().url().optional(),

  // Logging
  LOG_LEVEL: z.enum(['debug', 'info', 'warn', 'error']).optional().default('info'),
  LOG_FORMAT: z.enum(['text', 'json']).optional().default('text'),

  // [obs-w5] Observability log hardening. All default ON; only the literal
  // 'false' opts out — kill switches, not enablement flags.
  // Redaction is read directly from process.env in the @bash/shared logger
  // (which can't import this server schema); declared here for prod docs/typing.
  LOG_REDACTION_V1: z.string().optional(),
  // One structured access-log line per HTTP response (health/poll routes excluded).
  HTTP_ACCESS_LOG_V1: z.string().optional(),
  // Access-log lines go to stdout/Railway by default; set 'true' to ALSO persist
  // them to log_events (off by default — one row per request would outrun the
  // retention cap and bloat the billing OLTP DB). Read in log-sink.service.ts.
  HTTP_ACCESS_LOG_PERSIST: z.string().optional(),

  // [obs-w4b] Sample rate (0..1) for the SERVER_TOOL_EXECUTED product-analytics
  // mirror — every tool already emits a 100% $ai_span, so this event is sampled to
  // cap volume. Default 0.25 when unset; read directly from process.env in
  // sandbox-relay.service (declared here for prod docs/typing).
  SERVER_TOOL_EVENT_SAMPLE_RATE: z.string().optional(),

  // Node environment
  NODE_ENV: z.enum(['development', 'production', 'test']).optional().default('development'),

  // P5.6 — server role for the (dark) web/worker split. 'all' (default) runs
  // everything exactly as today; 'web' skips the BullMQ workers + Nami pipeline
  // crons; 'worker' runs ONLY those + a health endpoint and skips HTTP routes /
  // socket / relay. Intentionally a permissive string (NOT a z.enum) so an
  // unknown value never crashes boot — resolveServerRole() fails OPEN to 'all'
  // with a loud log (see apps/server/src/config/server-role.ts). Unset = 'all' =
  // byte-identical.
  SERVER_ROLE: z.string().optional(),

  // C3a — which ENTRY this process is: 'api' (default when unset; dist/index.js,
  // byte-identical) or 'worker' (dist/worker.js: ONLY the Nami dream lanes + /health,
  // the dedicated Railway worker service the founder ruled for on 29 Aug 2026).
  // Orthogonal to SERVER_ROLE above. Same permissive-string reasoning: laneSetFor()
  // fails OPEN to 'api' with a loud log (apps/server/src/config/process-role.ts), and
  // each entry refuses a role that is not its own.
  VESTRA_PROCESS_ROLE: z.string().optional(),

  // Supabase configuration (optional - falls back to in-memory if not set)
  SUPABASE_URL: z.string().url().optional(),
  SUPABASE_SERVICE_KEY: z.string().min(1).optional(),
  SUPABASE_ANON_KEY: z.string().min(1).optional(),

  // Dedicated HMAC secret for sandbox→server tokens (S2). Decoupled from the
  // Supabase service key so the key can be rotated independently. Optional:
  // falls back to SUPABASE_SERVICE_KEY during the rollover window.
  SANDBOX_TOKEN_SECRET: z.string().min(1).optional(),

  // Blaxel sandbox configuration
  BL_API_KEY: z.string().min(1).optional(),
  BLAXEL_API_KEY: z.string().min(1).optional(),
  USE_BLAXEL_SANDBOX: z
    .string()
    .optional()
    .transform((val) => val === 'true'),
  SANDBOX_LIFECYCLE_V2: z
    .string()
    .optional()
    .transform((val) => val === 'true'),
  SANDBOX_RUNTIME_V2: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // The ONE sanctioned automated sandbox delete besides account deletion (founder
  // word 2026-08-20): a wake that finds a FAILED/ERROR husk deletes it and
  // re-provisions once (sandbox/lifecycle/failed-recreate.ts, server-read via
  // failedSandboxRecreateOn). Server-only — NEVER add to FEATURE_ENV_FLAGS; the
  // sandbox gets no say in whether it is deleted.
  // Default OFF leaves the husk ALONE. That is NOT the pre-PR behavior: the call
  // site this replaced deleted unfenced. OFF is the honest dark ship — no
  // automated delete at all — so the key must be SET for the fix to be live.
  FAILED_SANDBOX_RECREATE_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // Studio content engine P0: gates the media-generation surfaces (intent router,
  // /api/media/submit, completion + polling, the media SDK tool). Boolean-typed so
  // resolveFeatureEnvs renders it 'true' when set and OMITS it when OFF — an OFF flag
  // never enters the env hash, so the OFF env-version stays byte-identical. Server
  // reads contentEngineOn() (release/content-engine-flag.ts); sandbox reads its
  // process.env mirror (harness/content-engine-flag.ts). Default OFF.
  CONTENT_ENGINE_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // Creative Studio P1 relay master gate. Boolean-typed like CONTENT_ENGINE_V1 so
  // resolveFeatureEnvs renders 'true' when set and OMITS it when OFF. Server reads
  // studioOn() (release/studio-flag.ts); sandbox reads process.env (harness/studio-flag.ts).
  STUDIO_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // W4-2 injection screen (ring 2 of the injection rail): the runner asks the server's
  // cheap-lane screener before a side-effecting tool acts on a tainted turn. 'true' =
  // shadow (log + banner), 'block' = S4 block and tell. A VALUE flag, but not a plain
  // passthrough: the two live values cross verbatim and EVERYTHING else is dropped,
  // exactly as the old boolean transform dropped it, so no env hash moves on this deploy
  // (a plain string would start delivering a stray 'false'). Sandbox reads its
  // process.env mirror (harness/injection-screen.ts injectionScreenMode). Default OFF.
  INJECTION_SCREEN_V1: z
    .string()
    .optional()
    .transform((val) => (val === 'true' || val === 'block' ? val : undefined)),

  // The Jev reflex rail's fast lane. ON, the box may ask the server's two internal
  // routes for the next browser/Mac step instead of taking a whole model turn over it;
  // OFF, both routes 404 and nothing asks. Boolean-typed so resolveFeatureEnvs renders
  // it 'true' when set and OMITS it when OFF (the OFF env-version stays byte-identical).
  // Read `=== 'true'` on both sides (server: services/jev/jev-flags.ts). Default OFF.
  JEV_FAST_LANE_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),


  // Task Outputs (Pieces) master gate: present_output SDK tool, the /api/tasks/:taskId/outputs
  // route pair + artifacts read-gate split (server outputsOn), and the web Pieces tab. Boolean-
  // typed like CONTENT_ENGINE_V1 so resolveFeatureEnvs renders it 'true' when set and OMITS it
  // when OFF — the OFF env-version stays byte-identical. Sandbox reads its process.env mirror
  // (harness/outputs-flag.ts). Default OFF.
  OUTPUTS_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // PR-D T2 — boot-time first-turn warmup on the runner (ability view + native MCP
  // schemas), paid after the lease acquire instead of inside the user's first message.
  // Boolean-typed like OUTPUTS_V1, so resolveFeatureEnvs renders it 'true' when set and
  // OMITS it when OFF (OFF env-version byte-identical). Sandbox reads its process.env
  // mirror (boot/boot-prewarm-flag.ts). Default OFF.
  RUNNER_BOOT_PREWARM_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),
  // The workspace file watch moves into the box (spec 2026-09-06 train 2). Boolean-typed
  // like RUNNER_BOOT_PREWARM_V1 above: both read sites are a strict `=== 'true'`, so the
  // transform is safe and OFF is OMITTED by resolveFeatureEnvs — the OFF env-version stays
  // byte-identical and no existing sandbox is disturbed by adding the key.
  WORKSPACE_FILES_IN_BOX_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),
  // Sandbox storage by lifetime, phase 1 — the runner samples the box's headroom on the
  // lease beat and the server keeps the latest per box. Declared so parseServerEnv cannot
  // strip the key (the AGENT_TEAMS #1476 gap); delivery itself rides the gate helper
  // sandboxVitalsOn(), which reads process.env DIRECTLY, so a stale @bash/shared cannot
  // dark it either. PLAIN STRING, not the boolean transform above: the transform coerces
  // every non-'true' value (a '1' typed on a dashboard) to false, and resolveFeatureEnvs
  // omits false — the flag would then ship dark while every artifact says delivered. A
  // passthrough string is safe whatever the value, and both read sites compare === 'true'.
  SANDBOX_VITALS_V1: z.string().optional(),
  // Same program, phase 2 — the gate on the tools-on-volume ENVIRONMENT (the venv, the
  // uv/pip/npm/bun caches, the CPU torch backend; sandbox/tools-env-vars.ts). The
  // flag itself never leaves the server: toolsOnVolumeOn() reads it and getRuntimeEnvs
  // declares the env behind it, so the runner sees VIRTUAL_ENV, never this name. Declared
  // here anyway because parseServerEnv strips what it does not know, and a stripped key
  // reads as "off" with every artifact claiming otherwise. Plain string for the same
  // reason as the line above: a boolean transform coerces a '1' to false and
  // resolveFeatureEnvs omits false; the read site compares === 'true'.
  SANDBOX_TOOLS_ON_VOLUME_V1: z.string().optional(),
  // Same program, phase 3 — the sweeper's switch (tidy/sweeper.ts tidyMode). A VALUE
  // env with three positions (off | dry | true), not a boolean (Ruling R80): `dry`
  // walks the ladder and deletes nothing. Plain string per docs/runbooks/flags.md
  // ("shape: match the reader, not a house default") — the boolean transform would
  // coerce 'dry' to false and omit it, delivering OFF to a box the operator put in
  // observe mode. Forwarded verbatim; both halves parse it through the one shared
  // parser (@bash/shared parseTidyMode).
  SANDBOX_TIDY_V1: z.string().optional(),
  // The disk guard — off | dry | on. A VALUE env: plain string, never the boolean transform, which
  // would coerce 'on' and 'dry' to false and deliver OFF. Every read site compares 'on' / 'dry'.
  SANDBOX_DISK_GUARD_V1: z.string().optional(),
  // Engineer teammates, Projects P1 — the box reports its projects on the lease beat.
  // Boolean-typed like WORKSPACE_FILES_IN_BOX_V1: the one read site is a strict === 'true',
  // so the transform is safe and OFF is OMITTED (never enters the env hash).
  SANDBOX_PROJECTS_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),
  // Engineer teammates, Git train G2 — the box's GitHub credential helper and commit author.
  // Both box read sites are a strict === 'true' (the helper, git-identity.ts githubAppOn), so
  // the boolean transform is safe and OFF is OMITTED (never enters the env hash). The server's
  // own read (githubAppConfig) is flagOn over process.env, which this schema never touches.
  GITHUB_APP_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),
  // The agent wallet (spec 2026-10-15). Both read sites are a strict === 'true' (the server's agentWalletOn, the box's
  // agentWalletOn), so the boolean transform is safe and OFF is OMITTED (never enters the env hash).
  AGENT_WALLET_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),
  // Disk budget D2 — the cold archive's switch (services/archive archiveMode), the same three
  // positions as the sweeper's and the same parser. A VALUE env: plain string, never a
  // boolean transform, which would coerce 'dry' to false and deliver OFF.
  SANDBOX_ARCHIVE_V1: z.string().optional(),
  // Work holds the box, train 1 — the hold's switch (off | shadow | true) and its cohort
  // list. VALUE envs, forwarded verbatim; both halves resolve them through the one shared
  // resolver (@bash/shared resolveWorkHoldMode). Plain strings per docs/runbooks/flags.md.
  SANDBOX_WORK_HOLD_V1: z.string().optional(),
  SANDBOX_WORK_HOLD_USERS: z.string().optional(),
  // Same program — journal retention in days for the sweeper (sweeper.ts journalRetentionDays).
  // A VALUE env (a day count, not a boolean): plain string per docs/runbooks/flags.md
  // ("shape: match the reader, not a house default") — the read site parses a number,
  // so a boolean transform would be actively wrong here. Forwarded verbatim, not gated;
  // absent on the runner falls back to the shared JOURNAL_RETENTION_DAYS default (14).
  SANDBOX_JOURNAL_RETENTION_DAYS: z.string().optional(),
  // Same program, phase 5 — the layout migration on wake (off | dry | on). A VALUE env,
  // not a boolean: 'dry' and 'on' are both real arms and a boolean transform would coerce
  // either to false, at which point resolveFeatureEnvs OMITS the key and the migration is
  // dark while every artifact says delivered. Plain string per docs/runbooks/flags.md
  // ("shape: match the reader"): both read sites compare the raw string.
  SANDBOX_LAYOUT_MIGRATE_V1: z.string().optional(),
  // Same program, phase 4 — the three switches that let the server act on what the beat
  // reports: restart a box whose RAM layer is full, grow a box under memory pressure,
  // grow a volume that is filling. All three are read on THIS side only
  // (sandbox/storage-flags.ts), so the runner never sees these names — but they still
  // cross parseServerEnv, and a key it does not know is stripped and reads as "off"
  // (the AGENT_TEAMS #1476 gap). Plain strings for the same reason as the lines above:
  // every read site compares === 'true'.
  SANDBOX_DISK_FULL_RESTART_V1: z.string().optional(),
  SANDBOX_MEMORY_AUTOGROW_V1: z.string().optional(),
  VOLUME_AUTOGROW_V1: z.string().optional(),
  // Deploy-seam ladder 1 — engine-side severed-stream retry. The engine reads it with
  // optInEnabled ('1' OR 'true', agent-sdk policy/feature-flags.ts), so it crosses
  // VERBATIM: a boolean transform here would coerce a '1' dashboard value to false,
  // resolveFeatureEnvs would omit the key, and the flag would ship dark while every
  // artifact claimed it was delivered. The live value is 'true' in both envs, which
  // renders identically either way; unset is omitted either way. Pinned by the
  // flag-manifest lint's opt-in-shape assertion (apps/server tests/unit).
  NATIVE_STREAM_SEVERED_RETRY: z.string().optional(),

  // Turn reattach rail (2026-08-31). Both readers are strictly `=== 'true'`
  // (apps/sandbox runner/reattach-flag.ts, apps/server services/release/reattach-flag.ts),
  // so a boolean transform would be safe — plain passthrough is deliberate anyway: the
  // SERVER half reads the raw process.env key (zod-strip-proof), so verbatim delivery keeps
  // the runner reading the same string the server does. Unset is omitted either way, so the
  // OFF env-version stays byte-identical.
  TURN_REATTACH_V1: z.string().optional(),

  // Harness to nine L6 — crash recovery mid-turn. Read `=== 'true'` SANDBOX-side only
  // (apps/sandbox stream/crash-resume.ts); plain passthrough so it crosses verbatim, and unset
  // is omitted, so the OFF env-version stays byte-identical.
  RUNNER_CRASH_RESUME_V1: z.string().optional(),

  // A turn a graceful restart stopped continues on the next runner (rides L6). Read `=== 'true'`
  // SANDBOX-side only (apps/sandbox stream/crash-resume.ts); plain passthrough, unset omitted.
  PLANNED_RESTART_RESUME_V1: z.string().optional(),

  // Harness to nine X3 — the skills list budget. Read `=== 'true'` SANDBOX-side only (apps/sandbox
  // abilities/catalog-budget.ts); plain passthrough so it crosses verbatim, and unset is omitted,
  // so the OFF env-version stays byte-identical.
  SKILL_CATALOG_BUDGET_V1: z.string().optional(),

  // Harness-9 W1 — three sandbox-read flags that were set on both dashboards and never
  // forwarded (no FEATURE_ENV_FLAGS row). Their SHAPE follows their reader, not a house
  // default: a boolean transform here is only safe when the reader is strictly === 'true'.
  // COMPACTION_V2 is read by the ENGINE's optInEnabled ('1' OR 'true', agent-sdk
  // policy/feature-flags.ts), so it stays a plain string and crosses VERBATIM — a
  // transform to boolean would coerce a '1' dashboard value to false, resolveFeatureEnvs
  // would omit the key, and the flag would ship dark while every artifact claimed it was
  // delivered (the exact failure class this wave closes). Unset is omitted either way, so
  // the OFF env-version stays byte-identical. Pinned by env.schema.test.ts.
  COMPACTION_V2: z.string().optional(),
  // Sandbox reads this one as === 'true' (harness/session-flags.ts), so the boolean
  // render-'true'-when-ON / omit-when-OFF transform is safe — same as
  // NATIVE_STREAM_SEVERED_RETRY above.
  FILE_STALENESS_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),
  // VALUE flag: 'off' | 'shadow' — forwarded verbatim (the sandbox parses it, === 'shadow').
  // Kept a plain optional string so an unset value is omitted from the sandbox env.
  SHELL_EGRESS_V1: z.string().optional(),

  // Harness-9 W2 — five sandbox-read flags the flag-manifest lint found with no delivery
  // row at all. ALL FIVE are plain passthrough, and for the four kill-switches that is not
  // a style choice: the only value anyone ever sets on one is 'false', and a boolean
  // transform would render that as `false`, which resolveFeatureEnvs OMITS — the kill would
  // parse cleanly, deliver nothing, and leave the tool on. Unset is omitted either way, so
  // every OFF env-version stays byte-identical.
  // Tool-catalog kill-switches, read !== 'false' (apps/sandbox harness/session-flags.ts,
  // browser/browser-flags.ts, config.ts).
  SESSION_SEARCH_V1: z.string().optional(),
  SESSION_CREATE_V1: z.string().optional(),
  NAMI_OFFLOOP_SWEEP_V1: z.string().optional(),
  BROWSER_SNAPSHOT_V1: z.string().optional(),
  // VALUE flag: 'off' | 'on' — the sandbox matches === 'on' (browser/egress-guard.ts), so a
  // boolean would render 'true' and no-op the guard.
  HANDS_EGRESS_V1: z.string().optional(),

  // Prompt-cache tiering (Prompt Assembly v3 PR-C) — fleet/session system cache
  // blocks on the native arm. Boolean-typed like OUTPUTS_V1 so resolveFeatureEnvs
  // renders it 'true' when set and OMITS it when OFF — the OFF env-version stays
  // byte-identical. Sandbox reads its process.env mirror
  // (harness/cache-tiering-flag.ts). Default OFF.
  CACHE_TIERING_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // Cross-task prompt cache (speed race gap 1): the per-user Agent roster moves out of
  // the tools block so tools + the fleet system prefix are shared across users. Read
  // `=== 'true'` (sandbox harness/cache-tiering-flag.ts), so a boolean transform is
  // safe: resolveFeatureEnvs renders 'true' when set and OMITS it when OFF. Default OFF.
  CROSS_TASK_CACHE_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // Creations P3 registration hardening — nested under OUTPUTS_V1. Gates the files/
  // Write/Edit auto-present wrapper (sandbox) + source/subkind/provenance column writes
  // and observe-only quota (server present-output.service). Boolean-typed like OUTPUTS_V1
  // so resolveFeatureEnvs OMITS it when OFF (OFF env-version byte-identical). Default OFF.
  CREATIONS_REGISTRATION_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // Creations P4 thumbnail render lane — server-only (deliverToSandbox false). Gates the
  // creation-thumbs BullMQ queue (server creationsThumbsOn): boots the worker, enables the
  // enqueue seam, and reads task_artifacts.thumb_* columns. Boolean-typed like OUTPUTS_V1.
  // Unset = OFF = no queue init, no worker, no new reads (byte-identical). Default OFF.
  CREATIONS_THUMBS_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // Knowledge/Uploaded thumbnail render lane — server-only (deliverToSandbox false).
  // Gates the file-thumb job kind on the same creation-thumbs BullMQ queue (server
  // fileThumbsOn): enqueues on confirmUpload, renders image/video (non-SVG) posters,
  // flips file_metadata.thumb_*. Boolean-typed like CREATIONS_THUMBS_V1.
  // Unset = OFF = no enqueue, no new reads (byte-identical). Default OFF.
  FILE_THUMBS_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // Output doc — the distill of the task rest (typed blocks at
  // tasks.output_doc; server release/output-doc-flag.ts outputDocOn).
  OUTPUT_DOC_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // Done rail door 1 — the request_user_input resident native tool (sandbox
  // harness/request-user-input-flag.ts). Delivered to the sandbox via
  // resolveFeatureEnvs; unset = OFF = tool absent, prompt byte-identical.
  REQUEST_USER_INPUT_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // The Vestra bridge (spec 2026-09-28 §6.4) — sandbox harness/engines/cli-common/bridge/
  // bridge-flag.ts engineBridgeEnabled (=== 'true'). Delivered via resolveFeatureEnvs; unset =
  // OFF = no bridge, the Claude Code argv byte-identical.
  ENGINE_BRIDGE_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // The lossless journal mirror (harness to nine R1) — sandbox stream/relay-blob-store.ts
  // losslessMirrorEnabled (=== 'true'). Delivered via resolveFeatureEnvs; unset = OFF = the
  // plain mirror, its rows byte-identical.
  JOURNAL_LOSSLESS_MIRROR_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // Rebuild the journal from its last compaction (harness to nine R2) — sandbox
  // stream/query-manager.ts _getOrCreateJournal (=== 'true'). Delivered via resolveFeatureEnvs;
  // unset = OFF = no checkpoint written, a full read on every open, byte-identical.
  JOURNAL_CHECKPOINT_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),
  // One writer per journal file — sandbox stream/journal-owners.ts journalOneWriterOn (=== 'true').
  // Delivered via resolveFeatureEnvs; unset = OFF = no registry, no lock file, byte-identical.
  JOURNAL_ONE_WRITER_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),
  // Fork a task from any turn (harness to nine R3) — server flagOn and sandbox
  // stream/fork-anchor.ts taskForkOn, both strict === 'true'. Delivered via resolveFeatureEnvs;
  // unset = OFF = the fork route 404s and no turn records an anchor, byte-identical.
  TASK_FORK_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),
  // Rewind a task to any turn (harness to nine R4b) — server flagOn and sandbox
  // stream/fork-anchor.ts taskRewindOn, both strict === 'true'. Unset = OFF = byte-identical.
  TASK_REWIND_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),
  // Continue anywhere, slice 1 — every read site is a strict === 'true' (server first-delivery.ts,
  // sandbox services/continuity.ts), so the boolean transform is safe and OFF is OMITTED (never
  // enters the env hash).
  CONTINUITY_JOURNAL_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // Live Localhost Preview master gate: announce_preview native tool, the
  // POST /api/tasks/:taskId/preview route (server appPreviewOn), and the reserved
  // app-preview Blaxel port. Boolean-typed like OUTPUTS_V1 so resolveFeatureEnvs
  // renders it 'true' when set and OMITS it when OFF — the OFF env-version stays
  // byte-identical. Sandbox reads its process.env mirror (harness/app-preview-flag.ts).
  // Default OFF.
  APP_PREVIEW_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // [ow3] Telemetry ingest (POST /api/telemetry) — the authed fan-out door for
  // dark client surfaces. Default OFF: the route 404s (byte-identical absence,
  // no auth, no fan-out) and the client no-ops. The caps below are the cost
  // controls (OD9 meter-every-emitter); all env-tunable with sane defaults.
  TELEMETRY_INGEST_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),
  // Max events accepted per POST (batch cap). Mirrors the journal-mirror MAX_ROWS.
  TELEMETRY_MAX_BATCH: intWithDefault(200),
  // Per-event byte ceiling; oversize props are replaced with a truncation stub
  // rather than sinking the batch (journal-mirror capEntry precedent).
  TELEMETRY_MAX_EVENT_BYTES: intWithDefault(16_000),
  // Per-user ingest rate limit (requests/min), enforced in-handler after the
  // token resolves the userId — the global @fastify/rate-limit is per-IP only.
  TELEMETRY_RATE_LIMIT_PER_MIN: intWithDefault(120),
  // (userId,event_id) idempotency LRU size — bounds the in-memory dedupe window.
  TELEMETRY_DEDUPE_LRU_SIZE: intWithDefault(5000),

  // Bedrock Slice 2 (M3): rotation, not recycle. Effective ONLY when
  // SANDBOX_RUNTIME_V2 is also true (see release/rotation-flag.ts) — its own
  // kill-switch for the hot wake-path rewire so a rotation regression rolls back
  // without disabling the proven proof/reconcile machinery. Default OFF — the OFF
  // path is byte-identical (System A wake-time upgrade block + deploy fan-out run
  // exactly as today). Spec §4.4.
  SANDBOX_ROTATION_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // Bedrock Slice 2: how long a relay may hold a lease past a pending convergence
  // before the sweeper politely requests a turn-boundary drain (spec §4.4). Only
  // consulted when rotation is on; degrades to alarm-only when TASK_INBOX_V1 is off.
  SANDBOX_DRAIN_DEADLINE_MS: intWithDefault(30 * 60 * 1000),

  // Fleet truth check (staleness program Layer 3): how long a LIVE runner may
  // self-report a release behind the channel's intended release before it pages.
  // Alarm-only; 0 (or negative) disables the sweeper entirely (rollback knob).
  FLEET_TRUTH_STALL_MS: intWithDefault(5 * 60 * 1000),

  // P4.6: global cap on concurrent turn-boundary converges per server process. Each
  // converge is a Blaxel control-plane burst (materialize/PUT + runner respawn), so a
  // post-deploy sweep that flags every awake relay must not fan out O(active users)
  // uncoordinated calls at once. A relay that can't get a slot at its turn boundary
  // simply serves the turn and retries at a later boundary. Floored at 1 at the read.
  SANDBOX_CONVERGE_MAX_CONCURRENT: intWithDefault(3),

  // P4.6: max per-user randomized delay (ms) before a sweep-flagged awake relay becomes
  // converge-eligible, so a deploy doesn't synchronize the herd onto the same turn
  // boundaries. 0 disables jitter (arm synchronously — byte-identical to pre-P4.6).
  SANDBOX_CONVERGE_SWEEP_JITTER_MS: intWithDefault(120 * 1000),

  // Bedrock Slice 1 (M1): durable task inbox. Default OFF — the OFF path is
  // byte-identical (no append, forward-dedup Map used as today, no inboxSeq on
  // the wire). Spec: docs/superpowers/specs/2026-07-03-bedrock-declarative-
  // sandbox-control-plane.md §4.2.
  TASK_INBOX_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // Composer M1: per-user daemon arm — lifelong DB thread + situation-ledger
  // context assembly + prompt-cache pyramid. Default OFF — the OFF path is
  // byte-identical (in-memory threads, plain-string system prompt, no ledger
  // taps). Plan: bash-notes/composer-m0m1-plan-2026-07-11.md.
  COMPOSER_DAEMON_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // home-streams: emit board:task_delta to user rooms (server-side gate).
  BOARD_STREAM_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // story spine: mint task_story rows + snapshot columns at the server seam
  // (bufferEvent + lifecycle observers). Rollback = flip off + redeploy.
  STORY_SPINE_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // Composer M2: router model override — swap the triage model on Railway
  // without a code PR (gpt-* rides the Responses lane, claude-* the Anthropic
  // lane). Unset = the code default. Gate any candidate on the
  // routing-precision eval before flipping (Luna failed it live 07-11).
  COMPOSER_ROUTER_MODEL: z.string().optional(),

  // Composer: reasoning effort on the OpenAI Responses lane — swap the dial on
  // Railway without a code PR. Unset = the code default ('high', founder call
  // 2026-08-06). Gate any candidate on the routing-precision eval before
  // flipping, same discipline as COMPOSER_ROUTER_MODEL.
  COMPOSER_REASONING_EFFORT: z.enum(['low', 'medium', 'high']).optional(),

  // Composer M2c: the control_app Drive verb (notch steers the main app
  // window). Default OFF — an OLDER installed desktop binary renders unknown
  // action kinds as a phantom task dot, so flip only after the desktop app
  // update ships (same ordering discipline as HANDS_OS_GATE).
  COMPOSER_APP_CONTROL_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // Composer voice: the delivery rail's finish message becomes the composer's
  // own sentence, with composeDeliveryText's text as the floor on every failure
  // (gateway/delivery/delivery-voice.ts). A PLAIN string, not a boolean
  // transform: the read site accepts '1' OR 'true' (the billing-flag idiom), and
  // a transform would coerce '1' to false and then resolveFeatureEnvs would omit
  // it — a flag that parses cleanly and delivers nothing.
  COMPOSER_VOICE_V1: z.string().optional(),

  // The Line: composer live voice on gpt-live-1. Read as '1' or 'true' at
  // apps/server/src/services/computer-use-flags.ts composerLiveOn, so a plain
  // string (a boolean transform would coerce '1' to false and strip it).
  COMPOSER_LIVE_V1: z.string().optional(),

  // Bash's phone, P1a (spec 2026-10-14). Read as '1' or 'true' at
  // apps/server/src/services/release/bash-phone-flag.ts bashPhoneOn, so a plain
  // string (a boolean transform would coerce '1' to false and strip it).
  BASH_PHONE_V1: z.string().optional(),
  // Its configuration, all optional: the phone door answers 503 or the can't-take
  // clip while any is unset, so an unconfigured environment boots as today.
  TELNYX_API_KEY: z.string().optional(),
  // Base64 of the raw Ed25519 key from Telnyx Mission Control (webhook signatures).
  TELNYX_PUBLIC_KEY: z.string().optional(),
  TELNYX_CONNECTION_ID: z.string().optional(),
  // The relay's https origin; the media and event sockets use its wss twin.
  PHONE_RELAY_URL: z.string().optional(),
  // Shared with the relay: its bearer secret and the stream tickets' HMAC key.
  PHONE_RELAY_SECRET: z.string().optional(),
  // The phone's OpenAI project (its key lives only on the relay): billing truth sums it.
  OPENAI_PHONE_PROJECT_ID: z.string().optional(),
  // Unassigned numbers the pool keeps per country (a whole number; unset = 20).
  // Staging sets 2: every pooled number is rented from the carrier.
  PHONE_POOL_TARGET: z.string().optional(),

  // Box hosts, Plan 2c: the boat machine adapter (apps/server/src/services/hosts/boat). Registered at
  // boot only when BOAT_API_KEY is set; BOAT_TEMPLATE is the named snapshot users' machines are created
  // from (apps/sandbox/box/boat/build-template.mjs prints it); BOAT_MACHINE_TYPE is the operator's override of their
  // size (unset = by plan: free and lite default, pro and above large, hosts/boat/machine-type.ts). Read from
  // process.env by hosts/boat/register-boat.ts. Server-only: never sent to a box.
  BOAT_API_KEY: z.string().optional(),
  BOAT_TEMPLATE: z.string().optional(),
  BOAT_MACHINE_TYPE: z.string().optional(),

  // Gateway PR2: an inbound Telegram message drives one COMPOSER turn (same
  // lifelong thread, history and telemetry as a web ask) instead of being
  // pinned to a conversation task and forwarded to a live agent. Default OFF —
  // the OFF path is byte-identical (routing.service + forwardToAgent exactly as
  // today). Binding and identity resolution are outside the flag: they run the
  // same on both arms.
  GATEWAY_COMPOSER_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // Bedrock Slice 3 (M2): runner lease + presence-as-projection. Effective ONLY
  // when SANDBOX_RUNTIME_V2 is also true (see release/lease-flag.ts) — its own
  // kill-switch so a lease/presence regression rolls back without disabling the
  // proven proof/reconcile/rotation machinery. Default OFF — the OFF path is
  // byte-identical (no lease RPCs, no expiry sweep, legacy warm-flag presence
  // derivation runs exactly as today). Spec §4.3.
  SANDBOX_LEASE_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // Bedrock Slice 5 (Phase 1): the inbound local-runner transport. Gates the
  // GET /api/internal/runner/ws HTTP-Upgrade route (404/close when off). This is
  // a DEPENDENT flag — it hard-requires the whole Bedrock control plane
  // (SANDBOX_RUNTIME_V2 && TASK_INBOX_V1 && SANDBOX_LEASE_V1), because a runner
  // that can't rest messages in the inbox or fence via the lease reintroduces the
  // silent-loss + duplicate-runner incidents on a NEW environment. localRunnerOn()
  // (release/local-runner-flag.ts) enforces the conjunction and refuses (loud
  // log) on a partial combination. Default OFF — the OFF path never attaches the
  // upgrade listener (byte-identical). Spec §4.5/§4.7.
  LOCAL_RUNNER_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // Two-brain P0: compute (sandbox wall-time) is never billed for sessions served by
  // the user's own machine (runner-inbound relays). Token costs are untouched.
  LOCAL_COMPUTE_FREE_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // Two-brain P2: capability routing replaces the placement pin as the interactive-
  // placement decider. When ON, classifyLocalPlacement routes by where a send was BORN
  // (surface) and whether it needs the machine (needsMachine) rather than by the user's
  // pin: born-native or needs-machine routes local, everything else cloud, and machine-
  // needing work whose laptop is asleep HOLDS in the durable inbox for the wake doorbell
  // instead of failing over. Default OFF — the OFF path leaves the legacy pin decider
  // byte-identical (no born read; the pin is consulted exactly as today).
  CAPABILITY_ROUTING_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // Bedrock Slice 5 (Phase 5): workspace-head handoff. Records the workspace head
  // (content hash + Storage artifact ref) at every turn boundary + on lease release
  // while holding the lease, and materializes-or-REFUSES the recorded head on lease
  // acquire before draining the inbox (the never-fork invariant, spec §4.7). A
  // DEPENDENT flag — workspaceHeadOn() (release/local-runner-flag.ts) hard-requires
  // the whole local-runner substrate (localRunnerOn()), since a head with no lease/
  // inbox/environment plumbing is meaningless. SEPARATE from LOCAL_RUNNER_V1 for
  // blast-radius isolation (mirrors S6's PLACEMENT_V1 discipline): head-recording
  // adds a tar + signed upload at the LIVE Blaxel turn boundary, so it gets its own
  // kill-switch independent of the (dark) transport. Default OFF — the OFF path adds
  // ZERO work to AGENT_END (no tar/upload/record) and acquire drains exactly as
  // today (byte-identical). Records on the Blaxel runner NOW so a laptop has a head
  // to materialize once local placement lands.
  WORKSPACE_HEAD_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // Bedrock Slice 6 (Phase 0): PlacementPolicy — the reconciler picks the
  // successor's environment (Blaxel ↔ local) at the safe points rotation already
  // defines (spec §4.7, "an update and a move are the same operation"). A
  // DEPENDENT flag — placementOn() (release/placement-flag.ts) hard-requires the
  // whole local-runner substrate (localRunnerOn()): AUTOMATIC placement decisions
  // are meaningless without a local runner to place onto, the lease that fences a
  // move, and the inbox that survives it. SEPARATE from LOCAL_RUNNER_V1 on purpose
  // (separate blast radius): S5 soaks manual/pin-driven placement first, S6 turns
  // on the automatic decision layer, so a policy regression rolls back without
  // disabling the (dark) transport. Default OFF — the OFF path never calls
  // decidePlacement and adds ZERO reads to the converge-plan hot path
  // (byte-identical). Spec §4.7.
  PLACEMENT_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // Cache-aware converge: defer a prompt-bytes-changing mid-session code swap until the
  // session is cache-cold (idle ≥ the 5-min prompt-cache TTL), so the swap never forces
  // an extra full-context cache re-write (the "cache-rebuild tax"). Effective ONLY when
  // rotation is also on (see release/cache-aware-converge-flag.ts) — the policy gates the
  // turn-boundary converge, which only exists under rotation. Default OFF — the OFF path
  // never consults the policy and adds ZERO reads to the converge hot path (byte-identical).
  CACHE_AWARE_CONVERGE_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // Runtime Release Plane: which channel to resolve intent from on AGENT_READY.
  // Defaults to 'staging' at the call site. Optional so existing deploys need no change.
  RUNTIME_RELEASE_CHANNEL: z.string().optional(),

  // Bedrock Slice 4: the standing chaos-drill suite. Default OFF — the OFF path
  // never creates the scheduler interval (byte-identical). A code tripwire in
  // drill-flag.ts additionally refuses to run against prod regardless of this
  // flag. Spec §5 + §8-Slice-4. STAGING-ONLY, manual flip with Keshav in the loop.
  DRILL_SUITE_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),
  // The dedicated drill user every destructive drill helper is hard-bound to
  // (never a param) — the one-user blast radius. Drills refuse to run when unset.
  DRILL_USER_ID: z.string().optional(),
  // Liveness-probe model for drill turns (not delegated work) — cheap + flippable.
  DRILL_MODEL: stringWithDefault('claude-haiku-5-5'),
  // Per-run and per-UTC-day spend ceilings (cents). A run that would breach the
  // daily ceiling is skipped (skipped_budget); the run cap bounds a single drill.
  DRILL_RUN_BUDGET_CENTS: intWithDefault(25),
  DRILL_DAILY_BUDGET_CENTS: intWithDefault(500),

  // Platform API v1: the TEST credits a person's self-made company account starts with
  // (POST /v1/me/orgs; 1 credit = $0.01). 0 grants none; at most 10,000 ($100), so a typo
  // fails at boot instead of granting far more to every sign-up. Live credits are never
  // granted self-serve: going live stays a step with Vestra.
  PLATFORM_SELF_SERVE_TEST_CREDITS: intWithDefault(1000)
    .refine((val) => val >= 0, { message: 'Must be 0 or more' })
    .refine((val) => val <= 10_000, { message: 'Must be 10000 or less' }),

  // Phase 4 flag: when true, getLatestDeploy() reads from `releases` instead of
  // `deploy_versions`. Flipped to 'true' once the releases table is fully backfilled
  // and CI is writing all DeployVersion fields on every publish.
  RELEASE_PLANE_SOLE_SOURCE: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // Anthropic / Model configuration (passed to sandbox runtime)
  ANTHROPIC_BASE_URL: z.string().optional(),
  ANTHROPIC_API_KEY: z.string().optional(),
  MODEL: stringWithDefault('claude-sonnet-5-5'),

  // OpenAI API key (optional — injected into sandbox for OpenAI runtime tasks).
  // OPENAI_API_HOST overrides the default api.openai.com upstream for the proxy
  // (tests / staging); the credential proxy already reads it but the schema
  // never declared it — additive, optional, byte-identical.
  OPENAI_API_KEY: z.string().optional(),
  OPENAI_API_HOST: z.string().optional(),

  // Deepgram speech-to-text (optional — the server mints short-lived scoped
  // access tokens for the omnipresent voice card via /v1/auth/grant, so the
  // owner key never leaves Railway). Unset -> /api/voice/deepgram-token 503s
  // gracefully ('voice_unconfigured') and the card degrades. Not prod-required.
  DEEPGRAM_API_KEY: z.string().optional(),

  // ElevenLabs Scribe v2 Realtime speech-to-text (optional — the server mints
  // single-use realtime tokens for the omnipresent voice card so the owner key
  // never leaves Railway, and the client connects to ElevenLabs with the token).
  // Unset -> /api/voice/realtime-token 503s gracefully and the card degrades.
  // Not prod-required.
  ELEVENLABS_API_KEY: z.string().optional(),

  // Fireworks (open-model lane: GLM 5.2, DeepSeek V4, …) (optional — rides the OpenAI
  // Chat Completions API at a different base URL + key. The credential proxy injects
  // FIREWORKS_API_KEY server-side exactly like OPENAI_API_KEY; the real key never
  // leaves Railway). FIREWORKS_BASE_URL is the sandbox-facing proxy URL (carries
  // Fireworks' /inference/v1 path); FIREWORKS_API_HOST overrides the upstream host
  // (default api.fireworks.ai) for the proxy (tests / staging).
  FIREWORKS_API_KEY: z.string().optional(),
  FIREWORKS_BASE_URL: z.string().optional(),
  FIREWORKS_API_HOST: z.string().optional(),

  // TypeSafe System One — the Jev reflex lane's vendor (optional, SERVER-ONLY: it is
  // read at exactly one site, services/jev/jev-client.ts, and is never delivered to a
  // box. The box asks our two internal routes; the credential stays on Railway).
  // Absent, every Jev call returns the fault `no_api_key` without making a request, so
  // the whole rail falls through to what the product does today.
  // TYPESAFE_BASE_URL overrides the endpoint (tests / staging).
  TYPESAFE_API_KEY: z.string().optional(),
  TYPESAFE_BASE_URL: z.string().optional(),

  // xAI (Grok direct lane) — OpenAI-Chat-Completions-compatible at api.x.ai.
  // Same credential-proxy contract as Fireworks.
  XAI_API_KEY: z.string().optional(),
  XAI_BASE_URL: z.string().optional(),
  XAI_API_HOST: z.string().optional(),

  // Google Gemini (gemini-3.8-flash, the Interactions API) — the credential proxy
  // injects GEMINI_API_KEY server-side as x-goog-api-key; GEMINI_API_HOST overrides
  // the upstream host (default generativelanguage.googleapis.com). Sandbox-facing:
  // GEMINI_AUTH_TOKEN is the per-user sandbox token (bearer to the proxy) and
  // GEMINI_BASE_URL the proxy URL — the two credential kinds the transport reads.
  GEMINI_API_KEY: z.string().optional(),
  GEMINI_API_HOST: z.string().optional(),
  GEMINI_AUTH_TOKEN: z.string().optional(),
  GEMINI_BASE_URL: z.string().optional(),

  // Exa.ai (optional — credential proxy injects this into sandbox web tool
  // requests; the proxy 502s gracefully when absent). EXA_API_HOST overrides
  // the default api.exa.ai upstream (tests / staging).
  EXA_API_KEY: z.string().optional(),
  EXA_API_HOST: z.string().optional(),

  // Web provider knobs (optional, non-secret) — threaded into the sandbox so the
  // provider chain is switchable server-side without a redeploy. WEB_PROVIDER_ORDER
  // is a comma list; exa is the only provider (Parallel retired 2026-09-25), so
  // unset or any other name resolves to exa in the SDK resolver. EXA_SEARCH_TYPE
  // selects Exa's search mode (e.g. "auto"/"neural").
  WEB_PROVIDER_ORDER: z.string().optional(),
  EXA_SEARCH_TYPE: z.string().optional(),

  // fal.ai configuration (optional)
  FAL_KEY: z.string().optional(),

  // GTM Lead Engine (optional) — shared org-level Apollo key for the internal
  // admin CRM (apps/dashboard /admin/gtm). ENABLE_GTM_LIVE_SEND gates the
  // Composio LinkedIn/Twitter send adapter; off = manual-queue fallback.
  APOLLO_API_KEY: z.string().optional(),
  ENABLE_APOLLO_CONNECTOR: z.enum(['true', 'false']).optional().default('false'),
  ENABLE_GTM_LIVE_SEND: z.enum(['true', 'false']).optional().default('false'),
  // Path token for the public Apollo waterfall-enrichment webhook
  // (POST /api/gtm/apollo-webhook/:token). Unset -> startWaterfall refuses to
  // run (nothing to deliver results to) and the route 404s every token.
  APOLLO_WEBHOOK_SECRET: z.string().min(1).optional(),

  // Tool search (optional)
  ENABLE_TOOL_SEARCH: z.string().optional(),

  // Native tool prewarm (optional) — when 'true', the sandbox caches + prewarms
  // the Composio MCP tool schemas so a native turn's tool build skips the ~600ms
  // connect()+listTools() round-trip. Passed through to the sandbox env.
  NATIVE_TOOL_PREWARM: z.string().optional(),

  // Harness to nine L4c — the connector schema cache v2 (stable key, no cliff, background
  // refresh, call-time fallback). Read `=== 'true'` by the runner (apps/sandbox
  // harness/mcp-schema-cache.ts) and by the server's MCP config serializer (spawn-mcp-config.ts,
  // which tags the Tool Router session id volatile). Plain passthrough; unset is omitted.
  MCP_SCHEMA_CACHE_V2: z.string().optional(),

  // Global admission limit (optional, numeric string) — sizes the sandbox's
  // process-global subagent admission semaphore, the process-wide cap on
  // concurrent :turnbatch model streams across ALL tasks in one sandbox process.
  // Unset = semaphore OFF (per-task 12-wide pools only). Threaded to the sandbox
  // via FEATURE_ENV_FLAGS; sandbox config.ts parses it (0/empty disables).
  GLOBAL_ADMISSION_LIMIT: z.string().optional(),

  // Bedrock Slice 5 (Phase 2, D6) — off-box kernel-journal mirror. STRING (not a
  // boolean transform) because the sandbox reads `=== '1'` (query-manager.ts): set
  // it to '1' to enable, unset to disable. Threaded via FEATURE_ENV_FLAGS so a
  // toggle moves the env-version hash and propagates to Blaxel sandboxes; OFF
  // (unset) is byte-identical. LOCAL runners always receive '1' via the
  // environment-spec regardless of this Blaxel-side flag.
  KERNEL_JOURNAL_MIRROR: z.string().optional(),

  // Harness 2.0 stage flags — sandbox-side behavior gates read by
  // apps/sandbox/src/config.ts (and journal-cost-seed.ts). ALL are STRING
  // passthroughs (never boolean transforms): the sandbox compares the literal
  // value — `=== '1'` for the engine flags, `=== 'true'` for the NAMI pair —
  // so the server must deliver the exact string it was given. Declared here so
  // parseServerEnv stops stripping them before FEATURE_ENV_FLAGS forwarding
  // (sandbox.service.ts); unset = omitted = byte-identical sandbox env.
  // RETIRED (always-on in code; dashboard keys and services.yaml lines removed 2026-10-04):
  // INTERRUPT_STEER_V2, SESSION_ACTOR_MAILBOX, TOOL_ERROR_TAXONOMY, SUBAGENT_SHARED_LOOP,
  // WEAVE_REPLAY, SUBAGENT_STEP_MEMO, JOURNAL_COST_SEED (crash-resume replay + no-rebill proven by
  // weave-tool.replay / agent-tool.step-memo / relay.journal-seed-billing unit tests).
  // NATIVE_REWIND (harness to nine R4): never wired, nothing read it; no longer declared.
  // Control-plane P3 — dark durable-Weave-HITL gate (sandbox reads `=== '1'`).
  WEAVE_HITL_DURABLE: z.string().optional(),
  // Orchestration walls — plain string passthrough (read `=== 'true'` sandbox-side).
  SUBAGENT_WALLS_V1: z.string().optional(),
  // Background helpers (Phase 2) — plain string passthrough (read `=== 'true'` sandbox-side).
  SUBAGENT_BACKGROUND_V1: z.string().optional(),
  // The team surface (Phase 3) — plain string passthrough (read `=== 'true'` sandbox-side).
  HELPER_TEAMWORK_V1: z.string().optional(),
  // A teammate may ask the person directly — plain string passthrough (read `=== 'true'`).
  TEAMMATE_ASKS_V1: z.string().optional(),
  // Agents-first lane A: abilities are skills and MCPs. Plain string passthrough (read `=== 'true'` on both sides).
  ABILITIES_SLIM_V1: z.string().optional(),
  // Media-refs S1 — per-lane request-build materialization gates (agent-sdk
  // policy/feature-flags.ts reads `=== '1' || === 'true'`). STRING passthroughs;
  // same #1476 declare-or-stripped contract: declared here or parseServerEnv
  // STRIPS them before FEATURE_ENV_FLAGS forwarding.
  MEDIA_FILE_ID_ANTHROPIC_V1: z.string().optional(),
  MEDIA_FILE_ID_OPENAI_V1: z.string().optional(),
  MEDIA_INLINE_CHAT_V1: z.string().optional(),
  MEDIA_INLINE_GEMINI_V1: z.string().optional(),
  // Edit argument repair + deterministic fuzzy match (agent-sdk feature-flags.ts
  // editHardenEnabled reads `=== '1' || === 'true'`). Same declare-or-stripped
  // contract as the gates above.
  EDIT_HARDEN_V1: z.string().optional(),
  // Credential broker for commands, harness to nine S1 (agent-sdk feature-flags.ts
  // credentialBrokerEnabled reads `=== '1' || === 'true'`). Same declare-or-stripped contract.
  SHELL_CREDENTIAL_BROKER_V1: z.string().optional(),
  // Dangerous-command rules as exact catalogs, harness to nine S5b (agent-sdk feature-flags.ts
  // securityPatternsV2Enabled reads `=== '1' || === 'true'`). Same declare-or-stripped contract.
  SECURITY_PATTERNS_V2: z.string().optional(),
  // The TURN key stays on the server, harness to nine B9b (server cf-turn-mint.ts
  // cfTurnServerMintOn and the box entrypoint both read `=== 'true'`). Same declare-or-stripped contract.
  CF_TURN_SERVER_MINT_V1: z.string().optional(),
  // Tool output budgets in tokens, per model (agent-sdk feature-flags.ts
  // toolOutputTokenBudgetEnabled reads `=== '1' || === 'true'`). Same
  // declare-or-stripped contract as the gates above.
  TOOL_OUTPUT_TOKEN_BUDGET_V1: z.string().optional(),
  // The non-progress tool watchdog, harness to nine L5 (agent-sdk feature-flags.ts
  // toolProgressWatchdogMode: 'true' | 'shadow', else off). A VALUE flag: plain string, so
  // 'shadow' is never coerced away. Same declare-or-stripped contract.
  TOOL_PROGRESS_WATCHDOG_V1: z.string().optional(),
  // One truncation marker for every tool result, harness to nine T5 (agent-sdk feature-flags.ts
  // toolResultShapeEnabled reads `=== '1' || === 'true'`). Same declare-or-stripped contract.
  TOOL_RESULT_SHAPE_V1: z.string().optional(),
  // Warm provider connections, harness to nine L4 (agent-sdk feature-flags.ts
  // warmConnectionsEnabled reads `=== '1' || === 'true'`). Same declare-or-stripped contract.
  WARM_CONNECTIONS_V1: z.string().optional(),
  // FIRST_BYTE_HEDGE_V1: read by the engine's optInEnabled ('1' or 'true'), so a plain string.
  FIRST_BYTE_HEDGE_V1: z.string().optional(),
  // Model-call retry by cause (agent-sdk feature-flags.ts retryByCauseEnabled reads
  // `=== '1' || === 'true'`). Same declare-or-stripped contract as the gates above.
  RETRY_BY_CAUSE_V1: z.string().optional(),
  // Hook event parity with Claude Code (agent-sdk feature-flags.ts hookParityEnabled reads
  // `=== '1' || === 'true'`). Same declare-or-stripped contract as the gates above.
  HOOK_PARITY_V1: z.string().optional(),
  // X2: user and marketplace hooks run, contained (apps/sandbox harness/contained-hooks.ts
  // userHooksEnabled reads `=== 'true'`). Same declare-or-stripped contract as the gates above.
  USER_HOOKS_V1: z.string().optional(),
  // X6: prompt and agent hooks (apps/sandbox harness/prompt-hooks.ts promptHooksEnabled reads
  // `=== 'true'`). Same declare-or-stripped contract as the gates above.
  PROMPT_HOOKS_V1: z.string().optional(),
  // Eliding wire projection + image-aware compaction ledger (agent-sdk
  // feature-flags.ts contextElisionEnabled reads `=== '1' || === 'true'`).
  // Same declare-or-stripped contract as the gates above.
  CONTEXT_ELISION_V1: z.string().optional(),
  // With OPENAI_TOOL_SEARCH_V1: OpenAI's hosted tool search; selects nothing on Claude
  // (agent-sdk feature-flags.ts toolDeferEnabled). Same declare-or-stripped contract as
  // the gates above.
  TOOL_DEFER_V1: z.string().optional(),
  // Deferred tool loading through the resident ToolSearch tool, on every lane (Claude
  // by tool_reference; agent-sdk feature-flags.ts toolSearchEnabled). Same
  // declare-or-stripped contract as the gates above.
  TOOL_SEARCH_V1: z.string().optional(),
  // Wide tool deferral, harness to nine T1b (agent-sdk feature-flags.ts toolDeferWideEnabled
  // reads `=== '1' || === 'true'`). Same declare-or-stripped contract as the gates above.
  TOOL_DEFER_WIDE_V1: z.string().optional(),
  // The GPT lane on OpenAI's own tool search (agent-sdk feature-flags.ts openaiToolSearchEnabled
  // reads `=== '1' || === 'true'`). Same declare-or-stripped contract.
  OPENAI_TOOL_SEARCH_V1: z.string().optional(),
  // Trimmed resident tool text, harness to nine T3 (agent-sdk feature-flags.ts
  // toolDescTrimEnabled reads `=== '1' || === 'true'`). Same declare-or-stripped contract.
  TOOL_DESC_LEAN_V1: z.string().optional(),
  // The 1-hour cache-write PRICE, read in the one Anthropic pricer on both sides (agent-sdk
  // feature-flags.ts billCacheWrite1hEnabled reads `=== '1' || === 'true'`). Same contract.
  BILL_CACHE_WRITE_1H_V1: z.string().optional(),
  // The off-loop charge hold, read on both sides (agent-sdk feature-flags.ts offloopChargeHoldEnabled
  // reads `=== '1' || === 'true'`). Same declare-or-stripped contract.
  OFFLOOP_CHARGE_HOLD_V1: z.string().optional(),
  TOOL_DESC_TRIM_V1: z.string().optional(),
  // A shell session that hands back, harness to nine T4 (agent-sdk feature-flags.ts
  // shellSessionEnabled reads `=== '1' || === 'true'`). Same declare-or-stripped contract.
  SHELL_SESSION_V1: z.string().optional(),
  // Nullable fields as type arrays, harness to nine T1b finish (agent-sdk feature-flags.ts
  // toolSchemaCompactEnabled reads `=== '1' || === 'true'`). Same declare-or-stripped contract.
  TOOL_SCHEMA_COMPACT_V1: z.string().optional(),
  // Shared and exclusive tools: the `concurrency` class decides which batch calls
  // overlap (agent-sdk feature-flags.ts toolConcurrencyClassesEnabled). Same
  // declare-or-stripped contract as the gates above.
  TOOL_CONCURRENCY_CLASSES_V1: z.string().optional(),
  // A failing model lane moves to its second route (harness to nine LOOP): the same proxy path
  // on the API plane (agent-sdk feature-flags.ts laneFallbackEnabled). Same declare-or-stripped
  // contract as the gates above.
  LANE_FALLBACK_V1: z.string().optional(),
  // Tools start mid-stream (harness to nine L1): a shared call runs the moment its
  // arguments are complete (agent-sdk feature-flags.ts toolsMidstreamEnabled). Same
  // declare-or-stripped contract as the gates above.
  TOOLS_MIDSTREAM_V1: z.string().optional(),
  // A dropped connection waits for the network (harness to nine LOOP): the main loop's request
  // retries past its bounded schedule with no cap (agent-sdk feature-flags.ts networkWaitEnabled).
  // Same declare-or-stripped contract as the gates above.
  NETWORK_WAIT_V1: z.string().optional(),
  // The read/write gate on top of L1 (harness to nine LOOP): an exclusive call starts at its
  // tool_ready once every earlier call settled (agent-sdk feature-flags.ts
  // toolsMidstreamRwEnabled). Same declare-or-stripped contract as the gates above.
  TOOLS_MIDSTREAM_RW_V1: z.string().optional(),
  // Silent deaths on the turn path become visible, harness to nine L5 (agent-sdk
  // feature-flags.ts silentDeathsEnabled reads `=== '1' || === 'true'`, the one read the
  // runner imports too). Same declare-or-stripped contract as the gates above.
  SILENT_DEATHS_V1: z.string().optional(),
  // Post-turn side calls off the input loop, harness to nine speed V0 (agent-sdk
  // feature-flags.ts postTurnOffPathEnabled reads `=== '1' || === 'true'`). Same
  // declare-or-stripped contract as the gates above.
  POST_TURN_OFF_PATH_V1: z.string().optional(),
  // Compaction at a turn's end, the Codex borrow (agent-sdk feature-flags.ts
  // compactionPostTurnEnabled reads `=== '1' || === 'true'`). Same declare-or-stripped contract.
  COMPACTION_POST_TURN_V1: z.string().optional(),
  // The memory sweep rides the loop's prompt cache, harness to nine (agent-sdk
  // feature-flags.ts sweepPrefixParityEnabled reads `=== '1' || === 'true'`). Same
  // declare-or-stripped contract as the gates above.
  SWEEP_PREFIX_PARITY_V1: z.string().optional(),
  // The parity sweep lowers its own turn with a per-message effort change (agent-sdk
  // feature-flags.ts sweepEffortMessageEnabled reads `=== '1' || === 'true'`). Same contract.
  SWEEP_EFFORT_MESSAGE_V1: z.string().optional(),
  // High-effort rows' sweeps ride the loop's cache by the history at stake (agent-sdk
  // feature-flags.ts sweepParityByPrefixEnabled reads `=== '1' || === 'true'`). Same contract.
  SWEEP_PARITY_BY_PREFIX_V1: z.string().optional(),
  // Subagent rounds reach the token ledger, harness to nine C5 (agent-sdk
  // feature-flags.ts subagentUsageLedgerEnabled reads `=== '1' || === 'true'`). Same
  // declare-or-stripped contract as the gates above.
  SUBAGENT_USAGE_LEDGER_V1: z.string().optional(),
  // The prefix is never rewritten mid-session, harness to nine C1 (agent-sdk
  // feature-flags.ts contextAppendEnabled reads `=== '1' || === 'true'`). Same
  // declare-or-stripped contract as the gates above.
  CONTEXT_APPEND_V1: z.string().optional(),
  // The per-task system tail rides behind the tools on the builds that render them after
  // the system message, speed race D (agent-sdk feature-flags.ts chatTailAfterToolsEnabled
  // reads `=== '1' || === 'true'`). Same declare-or-stripped contract as the gates above.
  CHAT_TAIL_AFTER_TOOLS_V1: z.string().optional(),
  // The memory rides as the first user input item on the Responses lane, speed race C'
  // (agent-sdk feature-flags.ts responsesMemoryInputEnabled reads `=== '1' || === 'true'`). Same
  // declare-or-stripped contract as the gates above.
  RESPONSES_MEMORY_INPUT_V1: z.string().optional(),
  // The memory block as the day's kernel and a per-task tail (agent-sdk feature-flags.ts
  // memoryDaySplitEnabled reads `=== '1' || === 'true'`). Same declare-or-stripped contract.
  MEMORY_DAY_SPLIT_V1: z.string().optional(),
  // What changed outside a warm turn, appended (agent-sdk feature-flags.ts contextAppendWarmEnabled
  // reads `=== '1' || === 'true'`). Same declare-or-stripped contract.
  CONTEXT_APPEND_WARM_V1: z.string().optional(),
  // Video understanding — uploaded video/audio/PDF reach the model: a `media`
  // block on the user turn, a native Files API reference on the Gemini lane and
  // the `watch` tool everywhere else (agent-sdk feature-flags.ts
  // videoUnderstandingEnabled). Same declare-or-stripped contract as the gates above.
  VIDEO_UNDERSTANDING_V1: z.string().optional(),
  // Abilities head shadow (spec §10-P1) — dark drive-head report gate (sandbox
  // query-manager.ts reads `=== 'true'`). Same #1476 declare-or-stripped contract:
  // declared here or parseServerEnv STRIPS it before FEATURE_ENV_FLAGS forwarding.
  ABILITIES_HEAD_V1: z.string().optional(),
  // Abilities converge (spec I4) — dark repair-then-serve gate (sandbox
  // converge-extras.ts reads `=== 'true'`). Same #1476 declare-or-stripped contract:
  // declared here or parseServerEnv STRIPS it before FEATURE_ENV_FLAGS forwarding.
  ABILITIES_CONVERGE_V1: z.string().optional(),
  // Ability Format v2 (Living Discs P4a) — dark parts + pressings + marker v3
  // gate (sandbox abilities/format-flag.ts reads `=== '1' || === 'true'`). Same
  // #1476 declare-or-stripped contract: declared here or parseServerEnv STRIPS it
  // before FEATURE_ENV_FLAGS forwarding.
  ABILITY_FORMAT_V2: z.string().optional(),
  // X1b — skills hash their whole served entry (hooks included) into the ability
  // checksum (server util/ability-format-flag.ts + sandbox abilities/format-flag.ts
  // hookFingerprintV1, both `=== '1' || === 'true'`). Same #1476 declare-or-stripped
  // contract: declared here or parseServerEnv STRIPS it before FEATURE_ENV_FLAGS forwarding.
  HOOK_FINGERPRINT_V1: z.string().optional(),
  // Ability Format v2 P4b — dark import-verb gate (sandbox abilities/verbs-flags.ts
  // reads `=== '1' || === 'true'`). Same #1476 declare-or-stripped contract: declared
  // here or parseServerEnv STRIPS it before FEATURE_ENV_FLAGS forwarding.
  ABILITY_IMPORT_V1: z.string().optional(),
  // Ability Format v2 P4b — dark reload-verb gate (epoch-contract converge-based
  // handler + turn gate; sandbox abilities/verbs-flags.ts reads `=== '1' || === 'true'`).
  // Same #1476 declare-or-stripped contract: declared here or parseServerEnv STRIPS it
  // before FEATURE_ENV_FLAGS forwarding.
  ABILITY_RELOAD_V1: z.string().optional(),
  // Ability convergence rail — the per-turn declared-head compare that makes a
  // LOST reload push self-heal (sandbox abilities/verbs-flags.ts
  // `abilitiesTurnRailV1()` reads `=== '1' || === 'true'`). Same #1476
  // declare-or-stripped contract: declared here or parseServerEnv STRIPS it
  // before FEATURE_ENV_FLAGS forwarding.
  ABILITIES_TURN_RAIL_V1: z.string().optional(),
  // Ability-hook KILL-SWITCH, inverted polarity: SET and registerAbilityHooks
  // registers nothing (sandbox harness/ability-hooks.ts `abilityHooksDisabledByEnv()`
  // reads `=== '1' || === 'true'`). Plain string, NOT a boolean transform — a
  // transform coerces '1' to false and resolveFeatureEnvs OMITS false, so the KILL
  // would parse cleanly and deliver nothing while every artifact said delivered
  // (docs/runbooks/flags.md § "Shape: match the reader"). Same #1476
  // declare-or-stripped contract: declared here or parseServerEnv STRIPS it before
  // FEATURE_ENV_FLAGS forwarding.
  DISABLE_ABILITY_HOOKS: z.string().optional(),
  // MCP seamless-connect — dark master gate (sandbox abilities/verbs-flags.ts
  // `abilityMcpV1()` reads `=== '1' || === 'true'`). Same #1476 declare-or-stripped
  // contract: declared here or parseServerEnv STRIPS it before FEATURE_ENV_FLAGS
  // forwarding.
  ABILITY_MCP_V1: z.string().optional(),
  // MCP stdio (npx) transport sub-gate, layered under ABILITY_MCP_V1 (sandbox
  // abilities/verbs-flags.ts `mcpStdioV1()` reads `=== '1' || === 'true'`). Same
  // #1476 declare-or-stripped contract: declared here or parseServerEnv STRIPS it
  // before FEATURE_ENV_FLAGS forwarding, and the stdio path stays dark in the
  // sandbox. NB: the decl only takes effect once the DEPLOYED @bash/shared dist is
  // rebuilt — a stale Railway build-layer cache can serve an old schema (07-14).
  MCP_STDIO_V1: z.string().optional(),
  // MCP OAuth transport sub-gate, layered under ABILITY_MCP_V1.
  // #1476 declare-or-stripped: must be declared here or parseServerEnv strips it before FEATURE_ENV_FLAGS forwarding.
  MCP_OAUTH_V1: z.string().optional(),
  // X5b (harness to nine): the mcp_resources tool (apps/sandbox harness/tools/mcp-resources-tool.ts
  // mcpResourcesEnabled reads `=== 'true'`). Declare-or-stripped, like the rest.
  MCP_RESOURCES_V1: z.string().optional(),
  // S6 (harness to nine): mcp_invoke results from user MCP servers fenced and tainted
  // (apps/sandbox mcp/user-mcp-registry.ts mcpResultFenceEnabled), and every deny/ask
  // rule matching the tool mcp_invoke runs (policy/permission-rules-loader.ts
  // mcpInnerRulesEnabled). Both read `=== 'true'`. Declare-or-stripped, like the above.
  MCP_RESULT_FENCE_V1: z.string().optional(),
  MCP_INNER_RULES_V1: z.string().optional(),
  // B6 (harness to nine): mcp_invoke judged as the MCP tool it runs (agent-sdk feature-flags.ts
  // mcpInvokeResolvedGateEnabled reads `=== '1' || === 'true'`). Declare-or-stripped, like the above.
  MCP_INVOKE_RESOLVED_GATE_V1: z.string().optional(),
  // #4367 fix 2a: approvals.always held on the connected-app path (agent-sdk feature-flags.ts
  // approvalsGateEnabled reads `=== '1' || === 'true'`). Declare-or-stripped.
  APPROVALS_GATE_V1: z.string().optional(),
  // X5 (harness to nine): MCP elicitation through the question card (apps/sandbox
  // mcp/mcp-elicitation.ts mcpElicitationEnabled reads `=== 'true'`). Declare-or-stripped.
  MCP_ELICITATION_V1: z.string().optional(),
  // Pre-registered Vestra OAuth app secret for GitHub's MCP (server-only; NEVER add to
  // FEATURE_ENV_FLAGS / LOCAL_ENV_ALLOWLIST / FLAG_REGISTRY — must never reach a sandbox).
  GITHUB_OAUTH_CLIENT_SECRET: z.string().optional(),
  // Ability Format v2 P4b import verb — server-only PAT for pulling private ability
  // repos/gists during import. NEVER add to FEATURE_ENV_FLAGS / LOCAL_ENV_ALLOWLIST /
  // FLAG_REGISTRY — it must never reach a sandbox.
  GITHUB_IMPORT_TOKEN: z.string().optional(),
  // MCP seamless-connect — server-only secret encryption key for storing connection
  // credentials (Task 5). NEVER add to FEATURE_ENV_FLAGS / LOCAL_ENV_ALLOWLIST /
  // FLAG_REGISTRY — it must never reach a sandbox.
  SECRET_ENCRYPTION_KEY: z.string().optional(),
  // Free credits once per person — server-only HMAC key for the email / Google-subject hashes in
  // free_credit_claims (apps/server/src/services/free-credit-claims.service.ts). NEVER ROTATE: a
  // new key makes every recorded deletion unmatchable. Unset = no claim is checked or recorded
  // (grants as before, logged at error). NEVER add to FEATURE_ENV_FLAGS / LOCAL_ENV_ALLOWLIST /
  // FLAG_REGISTRY — it must never reach a sandbox.
  FREE_CREDIT_CLAIM_KEY: z.string().optional(),
  // Control-plane P5 — dark Agent Teams gate (sandbox reads `=== '1'`, config.agentTeams).
  // MUST be declared here or parseServerEnv STRIPS it before FEATURE_ENV_FLAGS forwarding —
  // resolveFeatureEnvs then reads env.AGENT_TEAMS === undefined and OMITS it, so the sandbox
  // never sees the flag (config.agentTeams stays false) no matter the Railway value. The
  // #1476 delivery-list wiring (FEATURE_ENV_FLAGS + LOCAL_ENV_ALLOWLIST) is inert without this.
  AGENT_TEAMS: z.string().optional(),
  // Permissions v2 — dark access-levels gate (sandbox reads `=== '1'`,
  // feature-flags.ts accessLevelsEnabled). Same #1476 rule: declared here or
  // parseServerEnv strips it before FEATURE_ENV_FLAGS forwarding.
  ACCESS_LEVELS_V1: z.string().optional(),
  NAMI_CAPTURE_SIGNAL: z.string().optional(),
  NAMI_FIRSTHAND: z.string().optional(),
  // PR1 Nami Keeper — dark master gate (sandbox reads `=== 'true'`,
  // config.namiKeeper). MUST be declared here or parseServerEnv STRIPS it
  // before FEATURE_ENV_FLAGS forwarding (the same #1476 declare-or-stripped
  // contract as NAMI_FIRSTHAND above).
  NAMI_KEEPER: z.string().optional(),
  // C3a — the dream hand-off latch. Exactly 'true' on BOTH the api server and the
  // nami-worker service once the worker exists in an env takes the 12h dream-floor
  // sweep off the api box; absent (or anything else) the api box keeps sweeping, so
  // nothing breaks before the service is provisioned. The worker sweeps always.
  // Server-only (read by laneSetFor at boot), never forwarded to a sandbox.
  NAMI_WORKER_ENABLED: z.string().optional(),
  // Phase 1 one-brain rail — dark master gate for the evidence spine + tree
  // unification. Same #1476 declare-or-stripped contract as NAMI_KEEPER above:
  // without this line parseServerEnv strips it before FEATURE_ENV_FLAGS
  // forwarding, and the sandbox arm (task A5) would go dark with everything
  // upstream of it reporting delivered.
  NAMI_ONE_BRAIN: z.string().optional(),
  // Phase 4 Train 2 — retrieval re-pointed at the tree (namiRetrievalArm's fourth
  // arm; sandbox reads the string in harness/nami-retrieval-flag.ts, read site is
  // `=== 'true'`). Same #1476 declare-or-stripped contract as NAMI_ONE_BRAIN above.
  NAMI_TREE_RECALL_V1: z.string().optional(),
  // context_query pull arm ('off'|'true'|'server'; sandbox reads the string in
  // harness/nami-retrieval-flag.ts). PLAIN string, never a boolean transform:
  // 'server' would coerce to false and resolveFeatureEnvs OMITS false, so the
  // arm would parse cleanly and deliver nothing. Same #1476 declare-or-stripped
  // contract as NAMI_ONE_BRAIN above.
  NAMI_RETRIEVAL_ENABLED: z.string().optional(),
  // Vestrapedia read-loop — dark wiki-nav gate (sandbox reads `=== 'true'`,
  // config.namiWikiNav). MUST be declared here or parseServerEnv STRIPS it
  // before FEATURE_ENV_FLAGS forwarding (the same #1476 gap as AGENT_TEAMS above).
  NAMI_WIKI_NAV: z.string().optional(),
  // NAMI_WIKI_MODEL_FLOOR is retired: the person's model pick runs as picked; no longer declared.
  // Harness 2.0 numeric knobs (numeric strings, parsed sandbox-side —
  // config.ts rejects non-positive/non-finite values)
  AMBIENT_FUEL_TANK_USD: z.string().optional(),
  GLOBAL_NODE_LIMIT: z.string().optional(),

  // Sandbox post-task idle-grace window in ms (the rollback knob for the 5s
  // idle floor — raise without a redeploy). STRING passthrough: the sandbox's
  // ActivityManager reads process.env.IDLE_TIMEOUT_MS at import (default 7000).
  // Declared here so parseServerEnv keeps it for FEATURE_ENV_FLAGS forwarding;
  // unset = omitted = byte-identical env (sandbox uses its 7s default).
  IDLE_TIMEOUT_MS: z.string().optional(),

  // Cobrowsing enable step — dark browser gates (sandbox browser-flags.ts reads
  // `=== 'true'` for both, composed with BROWSER_SNAPSHOT_V1). Same #1476
  // declare-or-stripped contract: declared here or parseServerEnv STRIPS them
  // before FEATURE_ENV_FLAGS forwarding and the sandbox never sees the flags.
  // (BROWSER_VIEWER_LEASE needs no entry: server-read via raw process.env in
  // viewer-lease-cap.ts, never forwarded.)
  BROWSER_PERCEPTION_V2: z.string().optional(),
  BROWSER_SHARED_CONTROL: z.string().optional(),
  // Mirror method (BROWSER_MIRROR_V1): sandbox browser-flags.ts reads `=== 'true'`
  // to inject the rrweb recorder. Same declare-or-stripped contract as the two above.
  BROWSER_MIRROR_V1: z.string().optional(),

  // [ow1] Cobrowse telemetry — server-read only (raw process.env at call time in
  // cobrowse-telemetry.ts, like BROWSER_VIEWER_LEASE above; never forwarded to
  // the sandbox): master capture flag + the cobrowse:frame_qos PostHog sample
  // rate (numeric string, default 0.1).
  COBROWSE_TELEMETRY_V1: z.string().optional(),
  COBROWSE_QOS_SAMPLE_RATE: z.string().optional(),

  // Local in-app browser (dial-mode runners only; cloud runners ignore it —
  // sandbox browser-flags.ts reads `=== 'true'`). Same #1476 declare-or-stripped
  // contract: declared here or parseServerEnv STRIPS it before FEATURE_ENV_FLAGS
  // forwarding and the local runner never sees the flag.
  LOCAL_BROWSER_V1: z.string().optional(),

  // Embedded browser attach mode (dial-mode runners only, composed with
  // LOCAL_BROWSER_V1): the local lane attaches to the desktop shell's embedded
  // Chromium instead of launching Chrome for Testing (sandbox browser-flags.ts
  // reads `=== 'true'`). Same declare-or-stripped contract as above.
  EMBEDDED_BROWSER_V1: z.string().optional(),

  // Autopilot global kill-switch. Default OFF — autopilot is being subsumed by
  // Nami, so it is gated off at the source: no scan routines are created, no
  // suggestions/tasks, no double-billing. Set to 'true' to revive the feature.
  AUTOPILOT_ENABLED: z
    .string()
    .optional()
    .transform((val) => val === 'true'),

  // Agent SDK configuration
  MAX_TURNS: intWithDefault(100),

  // Redis (optional — enables Socket.io horizontal scaling)
  REDIS_URL: z.string().optional(),

  // PostHog analytics (optional)
  POSTHOG_API_KEY: z.string().optional(),
  POSTHOG_HOST: z.string().optional(),
  // One PostHog event per model call, harness to nine O1 (services/model-call-events.ts,
  // flagOn === 'true'). Server-side only — it reaches no sandbox.
  MODEL_CALL_EVENTS_V1: z.string().optional(),
  // Our own traces, harness to nine O2 (services/tracing/, flagOn === 'true'). Server-side only.
  // The endpoint and headers (a backend's key) are unset until a destination is chosen: until
  // then spans are counted and sent nowhere. The sample rate is 0 to 1 (default 1).
  OTEL_TRACES_V1: z.string().optional(),
  OTEL_EXPORTER_OTLP_TRACES_ENDPOINT: z.string().optional(),
  OTEL_EXPORTER_OTLP_HEADERS: z.string().optional(),
  OTEL_TRACES_SAMPLE_RATE: z.string().optional(),

  // Internal sandbox → server token (Phase 4 unified input pipeline).
  // The sandbox posts to /api/internal/interactions using this token.
  // Required in production; optional in dev so local runs without a token still work.
  VESTRA_INTERNAL_TOKEN: z.string().min(32).optional(),

  // Blaxel workspace — @blaxel/core reads this from env and sends it as the
  // x-blaxel-workspace header on every SDK request; the raw REST calls in
  // sandbox.service.ts must send it too (Task 3) or SDK and REST diverge
  // (the 2026-06 split-brain incident). optional-warn this slice; Task 14
  // promotes to required-prod after live Railway verification.
  BL_WORKSPACE: z.string().min(1).optional(),
  BLAXEL_SANDBOX_NAME: z.string().optional(),

  // Composio integrations. undefined -> opaque integration failures at use time.
  COMPOSIO_API_KEY: z.string().min(1).optional(),
  // Standard-Webhooks HMAC secret for Composio callbacks. Unset, the webhook
  // routes never register and the rail goes silently dark -> declared here so
  // typed env carries it, optional-warn at boot (config/env.ts).
  COMPOSIO_WEBHOOK_SECRET: z.string().min(1).optional(),
  // White-label: JSON {"toolkit":"ac_..."} map pinning Tool Router sessions to
  // our own OAuth clients (parsed in apps/server composio-auth-config-map.ts).
  // Unset -> Composio-managed auth configs, byte-identical behavior.
  COMPOSIO_AUTH_CONFIG_IDS: z.string().optional(),

  // Public URLs. PUBLIC_SERVER_URL falls back to RAILWAY_PUBLIC_DOMAIN.
  WEB_APP_URL: z.string().url().optional(),
  PUBLIC_SERVER_URL: z.string().url().optional(),
  RAILWAY_PUBLIC_DOMAIN: z.string().optional(),

  // Email (Resend). Unset -> admin/alert emails silently do not send.
  RESEND_API_KEY: z.string().min(1).optional(),
  ADMIN_FROM_EMAIL: z.string().optional(),

  // Agent email (spec 2026-10-14): one address per person on vestramail.com through SES. The channel
  // self-disables unless DOMAIN, REGION, both AWS keys, the bucket and the inbound topic are present
  // (gateway/email/config.ts). The keys are the server's own IAM user; nothing here reaches a box.
  AGENT_EMAIL_DOMAIN: z.string().min(1).optional(),
  AGENT_EMAIL_AWS_REGION: z.string().min(1).optional(),
  AGENT_EMAIL_AWS_ACCESS_KEY_ID: z.string().min(1).optional(),
  AGENT_EMAIL_AWS_SECRET_ACCESS_KEY: z.string().min(1).optional(),
  AGENT_EMAIL_S3_BUCKET: z.string().min(1).optional(),
  AGENT_EMAIL_S3_PREFIX: z.string().optional(),
  AGENT_EMAIL_SNS_TOPIC_ARN: z.string().min(1).optional(),
  AGENT_EMAIL_EVENTS_TOPIC_ARN: z.string().min(1).optional(),
  AGENT_EMAIL_CONFIGURATION_SET: z.string().min(1).optional(),
  AGENT_EMAIL_CAP_NEW_RECIPIENTS_PER_DAY: z.string().optional(),
  AGENT_EMAIL_CAP_SENDS_PER_DAY: z.string().optional(),
  AGENT_EMAIL_CAP_RECIPIENTS_PER_MESSAGE: z.string().optional(),
  // The rollout cohort (founder 2026-10-14): unset or empty = nobody (off for everyone); `*` = everyone;
  // otherwise a comma-separated list of Vestra user ids. Read only by agentEmailEnabledFor (config.ts).
  AGENT_EMAIL_USERS: z.string().optional(),

  // Slack. SIGNING_SECRET gates slash commands; ALERT_WEBHOOK is the sandbox
  // reliability alert channel (Task 12); SIGNUP_WEBHOOK posts new-user signup
  // notifications to #user-signups (set on prod only — unset = disabled).
  SLACK_SIGNING_SECRET: z.string().min(1).optional(),
  SLACK_ALERT_WEBHOOK: z.string().url().optional(),
  SLACK_SIGNUP_WEBHOOK: z.string().url().optional(),
  // Support bridge: bot token (xoxb-...) + channel ID of the support channel.
  // Both unset -> bridge silently disabled. Webhooks can't thread (no ts
  // returned), hence a bot token here.
  SLACK_SUPPORT_BOT_TOKEN: z.string().min(1).optional(),
  SLACK_SUPPORT_CHANNEL: z.string().min(1).optional(),
  // Billing journey cards (checkout started / subscribed / renewed) to the
  // subscriptions channel. Unset -> disabled.
  SLACK_SUBSCRIPTION_WEBHOOK: z.string().url().optional(),
  // @bash in Slack (spec 2026-09-28): the "Bash" Slack app, separate from the
  // Vestra Drift support app above (its own signing secret). All four present:
  // the Slack arm can run; any missing: it self-disables. Bot tokens are
  // stored encrypted with SECRET_ENCRYPTION_KEY, so no fifth key.
  SLACK_BASH_CLIENT_ID: z.string().min(1).optional(),
  SLACK_BASH_CLIENT_SECRET: z.string().min(1).optional(),
  SLACK_BASH_SIGNING_SECRET: z.string().min(1).optional(),
  SLACK_BASH_APP_ID: z.string().min(1).optional(),

  // WhatsApp over Twilio (gateway PR4). All optional: the route self-disables
  // when any of SID / token / from-number / public URL is unset, so an
  // unconfigured environment boots exactly as it does today.
  TWILIO_ACCOUNT_SID: z.string().optional(),
  TWILIO_AUTH_TOKEN: z.string().optional(),
  // E.164 sender, e.g. '+14155238886' (the adapter prefixes 'whatsapp:').
  TWILIO_WHATSAPP_FROM: z.string().optional(),
  // Sandbox era only. The BARE join phrase Twilio assigned, e.g.
  // 'join lucky-panda' — NOT a sentence. The connect card writes the sentence
  // around it ("First send <phrase> to this number, then send the code"), so a
  // full sentence here renders as a sentence inside a sentence. Unset on a
  // production sender, where no join step exists.
  TWILIO_WA_SANDBOX_JOIN: z.string().optional(),
  // The EXACT externally-visible webhook URL Twilio signs. It cannot be derived
  // from the request behind Railway's proxy (which rewrites host/proto), and a
  // URL that differs by one character verifies nothing — every real inbound
  // would 401.
  GATEWAY_WHATSAPP_PUBLIC_URL: z.string().optional(),

  // WhatsApp over Meta's Cloud API — the DIRECT transport, no reseller. When
  // the first four are all present the gateway rides Meta and Twilio is not
  // built at all (one WhatsApp adapter per process); with any of them missing
  // it falls back to Twilio, and with neither set WhatsApp self-disables.
  WHATSAPP_CLOUD_ACCESS_TOKEN: z.string().optional(),
  // The phone number's Graph id — NOT the phone number itself. The displayed
  // number is read back from Graph, so it is never configured twice.
  WHATSAPP_CLOUD_PHONE_NUMBER_ID: z.string().optional(),
  // The Meta APP secret (not the token): every inbound webhook is HMAC-SHA256
  // signed with it over the raw request body.
  WHATSAPP_CLOUD_APP_SECRET: z.string().optional(),
  // OURS to mint — any hard-to-guess string. Meta echoes it back on the GET
  // subscription handshake and will not deliver a message until it matches.
  WHATSAPP_CLOUD_VERIFY_TOKEN: z.string().optional(),
  // Optional per-deployment override of the pinned Graph version
  // (WHATSAPP_CLOUD_GRAPH_VERSION in the adapter). Unset uses the pin.
  WHATSAPP_CLOUD_GRAPH_VERSION: z.string().optional(),

  // iMessage over Photon's Spectrum platform. The first three are the gate:
  // with all of them present the iMessage gateway registers, with any missing
  // it self-disables (there is no second transport to fall back to).
  PHOTON_PROJECT_ID: z.string().optional(),
  // Outbound rides Photon's gRPC API, which is authenticated per PROJECT — the
  // secret is what mints the per-instance clients at first send.
  PHOTON_PROJECT_SECRET: z.string().optional(),
  // The per-webhook signing secret Photon mints when the webhook is registered.
  // Every inbound is HMAC-SHA256 signed with it over 'v0:<timestamp>:<raw body>'.
  PHOTON_WEBHOOK_SECRET: z.string().optional(),
  // The E.164 number users text to reach the agent. DISPLAY/COPY ONLY — Photon
  // routes outbound by project, so this never selects a sender; it exists so the
  // connect card can tell a human which number to open a chat with.
  PHOTON_IMESSAGE_LINE: z.string().optional(),

  // Billing truth (BILLING_TRUTH_V1) — provider billing APIs as reconciliation
  // anchor. Admin keys are a DIFFERENT class from the inference keys
  // (sk-ant-admin01-…, sk-admin-…). All optional: each adapter self-disables
  // when its prerequisites are missing.
  BILLING_TRUTH_V1: z.string().optional(),
  // The cache watch by the hour (CACHE_WATCH_V1, harness to nine C3): the alerts worker's lane
  // rate and rebuild pages and the waste digest line. Read live as `=== 'true'`.
  CACHE_WATCH_V1: z.string().optional(),
  ANTHROPIC_ADMIN_API_KEY: z.string().optional(),
  ANTHROPIC_WORKSPACE_ID: z.string().optional(),
  // Comma-separated Anthropic API key ids (apikey_…; ids, not secrets) whose usage IS
  // this environment's Anthropic spend. With no workspace pinned, the truth leg is these
  // keys' tokens at the card, proved daily against the org cost_report (isEnvScopedTruth).
  ANTHROPIC_BILLING_API_KEY_IDS: z.string().optional(),
  OPENAI_ADMIN_API_KEY: z.string().optional(),
  OPENAI_PROJECT_ID: z.string().optional(),
  // '1' declares OPENAI_PROJECT_ID is dedicated to THIS environment — set only
  // once the env has an OpenAI project of its own (isEnvScopedTruth).
  OPENAI_PROJECT_ENV_SCOPED: z.string().optional(),
  BLAXEL_ACCOUNT_ID: z.string().optional(),
  BLAXEL_BILLING_WORKSPACE: z.string().optional(),
  // Per-USER compute cost from the same Blaxel meter, grouped by resource_name
  // (BLAXEL_RESOURCE_COSTS_V1 = 'true'). Server-side only — it reaches no sandbox.
  BLAXEL_RESOURCE_COSTS_V1: z.string().optional(),
  // Leak SHADOW threshold: dollars in the newest 6 hourly buckets, per user, above
  // which the pass logs one warn line. Default 2.0; logs only, pages nobody.
  BLAXEL_LEAK_USD_PER_6H: z.string().optional(),
  FIREWORKS_ACCOUNT_ID: z.string().optional(),
  // Service-account key for /billing/summary — the inference FIREWORKS_API_KEY
  // is 403'd on management endpoints, so billing needs its own credential.
  FIREWORKS_BILLING_API_KEY: z.string().optional(),
  ALERT_BILLING_TRUTH_PCT: z.string().optional(),
  ALERT_BILLING_TRUTH_MIN_USD: z.string().optional(),
  // The truth gap's dollar floor (default $2) and the under-charge page line (default 0.10).
  ALERT_BILLING_TRUTH_MIN_GAP_USD: z.string().optional(),
  ALERT_BILLING_TRUTH_UNDER_PAGE_PCT: z.string().optional(),
  ALERT_BILLING_TRUTH_PCT_SOFT: z.string().optional(),
  ALERT_BILLING_TRUTH_STALE_HOURS: z.string().optional(),

  // Neko browser-streaming ICE servers (JSON array string).
  NEKO_ICE_SERVERS: z.string().optional(),

  // [obs-w1] Observability alert worker (apps/server/src/services/alerts). All
  // optional; the worker no-ops unless ALERTS_V1 !== 'false' AND at least one
  // sink is set (SLACK_ALERT_WEBHOOK above, or BETTERSTACK_INCIDENT_WEBHOOK
  // here — a generic JSON incident webhook that rings a phone for PAGE alerts).
  // Declared here or parseServerEnv STRIPS them (#1476); the worker reads
  // process.env live so a Railway change lands on the next tick. Thresholds
  // default to the spec's staging base rates.
  ALERTS_V1: z.string().optional(),
  // Comma-separated rule-id allowlist (e.g. 'billing_drift'). Set → only listed
  // rules dispatch; unset/blank → every rule dispatches.
  ALERTS_RULES: z.string().optional(),
  BETTERSTACK_INCIDENT_WEBHOOK: z.string().url().optional(),
  ALERT_SILENT_FAILURE_THRESHOLD: z.string().optional(),
  ALERT_FAILED_BURN_COUNT: z.string().optional(),
  ALERT_FAILED_BURN_RATE: z.string().optional(),
  ALERT_FAILED_BURN_RATE_MIN_TURNS: z.string().optional(),
  ALERT_ERROR_SPIKE_MULTIPLIER: z.string().optional(),
  ALERT_ERROR_SPIKE_MIN: z.string().optional(),
  ALERT_BILLING_DRIFT_PCT: z.string().optional(),
  ALERT_BILLING_DRIFT_MIN_USD: z.string().optional(),
  // The internal drift's dollar floor alongside its percentage (default $1).
  ALERT_BILLING_DRIFT_MIN_ABS_USD: z.string().optional(),
  // surface-NULL digest (Slack): fraction of agent_turns with surface IS NULL over
  // 24h, gated on a minimum turn volume. Defaults 0.10 / 20 turns.
  ALERT_SURFACE_NULL_RATE: z.string().optional(),
  ALERT_SURFACE_NULL_MIN_TURNS: z.string().optional(),
  // Runner wedge (PAGE): per-user error-severity error_events counts in a 60m
  // window, one knob per category. Both default to 1 — see alert-rules.ts for
  // why each arm's floor is where it is.
  ALERT_RUNNER_WEDGE_RELEASE_MIN: z.string().optional(),
  ALERT_RUNNER_WEDGE_DELIVERY_MIN: z.string().optional(),
  // Nami reconciler rules (T11o §5.3): the dream lane's own pager/digest set, read
  // over nami_dream_pass and the two error_events categories the pass and its web
  // tools write. Defaults 5 aborts/60m, 3 consecutive aborts per user, 25% of ≥4
  // passes cut by the deadline, half of ≥2 web passes spending their whole budget,
  // 8 distinct domains with ≤2 cites (the browsing signature), 5 provider failures
  // /60m. fetch_blocked has no knob — one SSRF refusal pages, by design.
  ALERT_RECONCILER_ABORT_BURST_MIN: z.string().optional(),
  ALERT_RECONCILER_WEDGED_MIN: z.string().optional(),
  ALERT_RECONCILER_DEADLINE_RATE: z.string().optional(),
  ALERT_RECONCILER_DEADLINE_MIN_PASSES: z.string().optional(),
  ALERT_RECONCILER_WEB_BUDGET_RATE: z.string().optional(),
  ALERT_RECONCILER_WEB_BROWSING_DOMAINS: z.string().optional(),
  ALERT_RECONCILER_WEB_BROWSING_MAX_CITES: z.string().optional(),
  ALERT_RECONCILER_WEB_PROVIDER_DOWN_MIN: z.string().optional(),
  // Sandbox headroom digest (storage by lifetime, phase 1): the percent of a
  // box's RAM layer / volume that puts it in the digest. Default 85.
  ALERT_SANDBOX_HEADROOM_PCT: z.string().optional(),
  // The morning digest (alert-digest.ts): when the statistic rules post, as one
  // message, "HH:MM" UTC. Default 03:30 (09:00 IST).
  ALERT_DIGEST_UTC: z.string().optional(),
  // Identical-page cooldown (alert hygiene). A wide-window PAGE rule re-pages at
  // most once per ALERTS_PAGE_COOLDOWN_MIN minutes for one ongoing condition,
  // UNLESS its measured value worsened by ≥ ALERTS_PAGE_ESCALATION_PCT percent vs
  // the last page (then it breaks through immediately). Defaults 120 / 25.
  ALERTS_PAGE_COOLDOWN_MIN: z.string().optional(),
  ALERTS_PAGE_ESCALATION_PCT: z.string().optional(),

  // [obs-w7] Online scorer worker (apps/server/src/services/scorer). Default
  // OFF — SCORER_V1 !== 'true' schedules nothing beyond a boot no-op warn.
  // Knobs are read live from process.env (a Railway change lands on the next
  // tick); declared here so parseServerEnv doesn't strip them (#1476).
  // SAMPLE_RATE defaults 0.25, DAILY_USD_CAP defaults 1.00 (judge spend is
  // metered per the standing meter-every-cost-surface rule). The scorer also
  // reads SYNTHETIC_PROBE_USER_ID (declared by obs-w2, #1660) to skip probe turns.
  SCORER_V1: z.string().optional(),
  SCORER_SAMPLE_RATE: z.string().optional(),
  SCORER_DAILY_USD_CAP: z.string().optional(),

  // Nami Phase 5 — the paired online eval worker (apps/server/src/services/nami-eval).
  // Default OFF: NAMI_EVAL_V1 !== 'true' schedules nothing beyond a boot no-op warn.
  // Knobs are read live from process.env (a Railway change lands on the next tick);
  // declared here so parseServerEnv doesn't strip them (#1476). SAMPLE_RATE defaults
  // 0.5 of eligible composer turns, DAILY_USD_CAP defaults 2.00 and is shared by twin
  // generation AND judging (the rows are the meter — nami_eval_pairs). The eval also
  // reads SYNTHETIC_PROBE_USER_ID (declared by obs-w2) to skip probe turns, through the
  // scorer's own probeUserIds.
  NAMI_EVAL_V1: z.string().optional(),
  NAMI_EVAL_SAMPLE_RATE: z.string().optional(),
  NAMI_EVAL_DAILY_USD_CAP: z.string().optional(),
  // The CI bearer for GET /api/admin/nami/proof and /proof/corpus: a memory PR fetches
  // the standing verdict and the frozen corpus with it instead of an admin session.
  // Unset => the endpoints accept an admin session only, which is the safe default.
  NAMI_EVAL_PROOF_TOKEN: z.string().optional(),

  // [obs-w3] Turn stall watchdog (agent-trace.service). Default OFF — the OFF
  // path is byte-identical (the sweep tick evaluates only the stranded-
  // accumulator TTL). ON: a live trace with no relay activity (no usage/tool/
  // log/stream event) for TURN_STALL_TIMEOUT_MS is finalized failed with
  // error_class='turn_stalled' + an error_events row — a hung turn on a live WS
  // was previously invisible forever. Observability-only; never touches billing.
  TURN_WATCHDOG_V1: z
    .string()
    .optional()
    .transform((val) => val === 'true'),
  // [obs-w3] Inactivity threshold for the watchdog above. Activity = ANY relay
  // event for the trace, so a streaming/tool-looping long turn can never trip it.
  // CAVEAT — why the default is 45min, not 15: ONE silent long-running tool call
  // (a single Bash exec with no output) emits zero relay events for its whole
  // duration; WS ping/pong is transport-level and deliberately does NOT count as
  // turn activity (a pong proves the process, not the turn — counting it would
  // blind the watchdog to its exact target, a hung turn on a live socket).
  TURN_STALL_TIMEOUT_MS: intWithDefault(45 * 60 * 1000),

  // Comma-separated synthetic-probe user ids, excluded from Mission Control
  // SLI queries so the external e2e probe never skews the real SLIs. [obs-w2]
  SYNTHETIC_PROBE_USER_ID: z.string().optional(),
}).superRefine((val, ctx) => {
  if (val.NODE_ENV !== 'production') return;

  // C3a — the nami worker (VESTRA_PROCESS_ROLE=worker, dist/worker.js) is the same
  // image with NODE_ENV=production, but it serves no HTTP API, proxies no sandbox and
  // registers no webhook. It needs Supabase and (until C11 moves the editor pass off
  // the user volume) Blaxel; requiring the api-only keys would make it HOLD secrets it
  // never reads. Same normalization as apps/server/src/config/process-role.ts.
  const isNamiWorker = val.VESTRA_PROCESS_ROLE?.trim().toLowerCase() === 'worker';

  for (const { key, reason } of PROD_REQUIRED_VARS) {
    if (isNamiWorker && !WORKER_PROD_REQUIRED_KEYS.has(key)) continue;
    if (!val[key]) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [key],
        message: `${key} is required when NODE_ENV=production (${reason})`,
      });
    }
  }

  // At least one Blaxel API key must be set in prod
  if (!val.BL_API_KEY && !val.BLAXEL_API_KEY) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['BL_API_KEY'],
      message: 'BL_API_KEY or BLAXEL_API_KEY is required when NODE_ENV=production (Blaxel sandbox lifecycle)',
    });
  }

  // Everything below is the api entry's surface.
  if (isNamiWorker) return;

  // Verified present in both Railway services (infra/railway/services.yaml) ->
  // safe to hard-require in prod.
  for (const key of ['WEB_APP_URL', 'COMPOSIO_API_KEY'] as const) {
    if (!val[key]) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [key], message: `${key} is required when NODE_ENV=production` });
    }
  }
  // PUBLIC_SERVER_URL relies on the RAILWAY_PUBLIC_DOMAIN fallback — require one.
  if (!val.PUBLIC_SERVER_URL && !val.RAILWAY_PUBLIC_DOMAIN) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom, path: ['PUBLIC_SERVER_URL'],
      message: 'PUBLIC_SERVER_URL or RAILWAY_PUBLIC_DOMAIN is required when NODE_ENV=production (Composio webhook callback URL)',
    });
  }

  // CORS_ORIGIN defaulting to localhost in prod would block the real frontend
  if (val.CORS_ORIGIN === 'http://localhost:3000') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['CORS_ORIGIN'],
      message: 'CORS_ORIGIN cannot default to http://localhost:3000 in production — set it to the frontend domain(s)',
    });
  }
});

export type ServerEnv = z.infer<typeof ServerEnvSchema>;

// =============================================================================
// Agent Environment Schema
// =============================================================================

export const AgentEnvSchema = z.object({
  // Anthropic authentication (one of these is required)
  // Direct key (legacy/local dev) or proxy auth token (production via Railway credential proxy)
  ANTHROPIC_API_KEY: z.string().optional(),
  ANTHROPIC_AUTH_TOKEN: z.string().optional(),
  ANTHROPIC_BASE_URL: z.string().optional(),

  // Server connection (used for SERVER_URL reference, agent now runs as server)
  SERVER_URL: stringWithDefault('http://localhost:3005'),

  // Agent WebSocket server port (REVERSE WEBSOCKET: agent listens, server connects)
  AGENT_PORT: intWithDefault(3001),

  // User identification (per-user sandbox model)
  USER_ID: stringWithDefault('anonymous'),

  // Session identification (required for routing)
  SESSION_ID: z.string().min(1, 'SESSION_ID is required'),
  AGENT_ID: stringWithDefault('general'),
  PERMISSION_MODE: z
    .enum(['default', 'acceptEdits', 'dontAsk', 'bypassPermissions'])
    .optional()
    .default('bypassPermissions'),

  // SDK configuration
  MODEL: stringWithDefault('claude-sonnet-5-5'),
  MAX_TURNS: intWithDefault(100),

  // Optional: SDK session ID for conversation resumption after restart
  RESUME_SDK_SESSION_ID: z.string().optional(),

  // Multi-agent configuration
  AGENT_SLUG: stringWithDefault('general'),
  IS_ORCHESTRATOR: z.string().optional().transform((val) => val === 'true'),
  WORKSPACE_PATH: stringWithDefault('/app/workspace'),
  INITIAL_TASK: z.string().optional(),
  LOG_LEVEL: z.enum(['debug', 'info', 'warn', 'error']).optional().default('info'),

  // Composio MCP server configuration (JSON string)
  MCP_SERVER_CONFIG: z.string().optional(),
}).superRefine((val, ctx) => {
  // The agent calls Anthropic either directly (ANTHROPIC_API_KEY) or via the
  // Railway credential proxy (ANTHROPIC_AUTH_TOKEN + ANTHROPIC_BASE_URL). One
  // of these auth paths must be present, otherwise every SDK query fails with
  // a 401 the moment the user sends a message.
  if (!val.ANTHROPIC_API_KEY && !val.ANTHROPIC_AUTH_TOKEN) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['ANTHROPIC_API_KEY'],
      message: 'Agent needs either ANTHROPIC_API_KEY (direct) or ANTHROPIC_AUTH_TOKEN + ANTHROPIC_BASE_URL (proxy) — neither is set',
    });
  }
  // If using proxy auth, the base URL is mandatory or the SDK will hit
  // api.anthropic.com with a token that doesn't belong there.
  if (val.ANTHROPIC_AUTH_TOKEN && !val.ANTHROPIC_BASE_URL && !val.ANTHROPIC_API_KEY) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['ANTHROPIC_BASE_URL'],
      message: 'ANTHROPIC_BASE_URL must be set when using ANTHROPIC_AUTH_TOKEN — proxy token is not valid for api.anthropic.com',
    });
  }
});

export type AgentEnv = z.infer<typeof AgentEnvSchema>;

// =============================================================================
// Frontend Environment Schema (Next.js public vars)
// =============================================================================

export const FrontendEnvSchema = z.object({
  // Server URL for Socket.io connection
  NEXT_PUBLIC_SERVER_URL: stringWithDefault('http://localhost:3005'),
});

export type FrontendEnv = z.infer<typeof FrontendEnvSchema>;

// =============================================================================
// Parse Helpers
// =============================================================================

/**
 * Parse and validate server environment variables
 * Throws ZodError with detailed message if validation fails
 */
export function parseServerEnv(env: NodeJS.ProcessEnv = process.env): ServerEnv {
  return ServerEnvSchema.parse(env);
}

/**
 * Parse and validate agent environment variables
 * Throws ZodError with detailed message if validation fails
 */
export function parseAgentEnv(env: NodeJS.ProcessEnv = process.env): AgentEnv {
  return AgentEnvSchema.parse(env);
}

/**
 * Parse and validate frontend environment variables
 */
export function parseFrontendEnv(env: Record<string, string | undefined>): FrontendEnv {
  return FrontendEnvSchema.parse(env);
}

// =============================================================================
// Safe Parse Helpers (returns result instead of throwing)
// =============================================================================

/**
 * Safely parse server env, returns { success, data } or { success, error }
 */
export function safeParseServerEnv(env: NodeJS.ProcessEnv = process.env) {
  return ServerEnvSchema.safeParse(env);
}

/**
 * Safely parse agent env, returns { success, data } or { success, error }
 */
export function safeParseAgentEnv(env: NodeJS.ProcessEnv = process.env) {
  return AgentEnvSchema.safeParse(env);
}
